/**
 * InsulaCharter - English Localization
 * Adapted from AlguerHouse for boat rental
 */

const en = {
  // ============================================
  // MARKUP INCLUSIVE (request/approval)
  // ============================================
  markupInclusive: {
    title: 'Inclusive pricing',
    subtitle: 'Request the platform markup to be included in the displayed price',
    rental: 'Rental',
    excursion: 'Excursions',
    statusActive: 'Active',
    statusPending: 'Pending approval',
    statusInactive: 'Not active',
    request: 'Request',
    requested: 'Request sent',
    requestError: 'Error sending request',
    approve: 'Approve',
    reject: 'Reject',
    approved: 'Request approved',
    rejected: 'Request rejected',
    actionError: 'Operation error',
    adminTitle: 'Inclusive markup',
    adminHint: 'Approve or reject inclusive pricing requests per branch.',
    vendorHint: 'Until approval the price stays additive (markup on top).',
  },
  // ============================================
  // METADATA & SEO
  // ============================================
  metadata: {
    siteTitle: 'InsulaCharter - Boat Rental in Sardinia',
    siteDescription: 'Find the perfect boat for your adventure in the crystal-clear waters of the Costa Smeralda and the coves of Sardinia.',
    loginTitle: 'Log In - InsulaCharter',
    loginDescription: 'Log in to your InsulaCharter account',
    registerTitle: 'Sign Up - InsulaCharter',
    registerDescription: 'Create your InsulaCharter account',
  },

  // ============================================
  // COMMON / SHARED
  // ============================================
  common: {
    all: 'All',
    loading: 'Loading...',
    error: 'Error',
    errorPrefix: 'Error: ',
    serverError: 'Server error',
    cancel: 'Cancel',
    confirm: 'Confirm',
    success: 'Success',
    save: 'Save',
    saving: 'Saving...',
    create: 'Create',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    searchNow: 'SEARCH NOW',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    free: 'Free',
    included: 'Included',
    day: 'day',
    days: 'days',
    guest: 'passenger',
    guests: 'passengers',
    passengers: 'passengers',
    boat: 'Boat',
    meters: 'meters',
    person: 'Person',
    persons: 'People',
    total: 'Total',
    price: 'Price',
    perDay: '/day',
    today: 'Today',
    uploading: 'Uploading...',
    sending: 'Sending...',
    hour: 'hour',
    hours: 'hours',
    loginRequired: 'Login required',
    from: 'From',
  },

  // ============================================
  // NAVBAR
  // ============================================
  navbar: {
    logo: 'InsulaCharter',
    maintenanceMode: 'MAINTENANCE MODE - The site is only visible to you',
    maintenanceShort: 'MAINTENANCE',
    exitMaintenance: 'Exit',
    accountActive: 'Active Account',
    roleHost: 'Boat Owner',
    roleGuest: 'Guest',
    adminPanel: 'Dashboard',
    adminSettings: 'Settings',
    myTrips: 'My Rentals',
    myRequests: 'My requests',
    logout: 'Log Out',
    login: 'Log In',
  },

  // ============================================
  // FOOTER
  // ============================================
  footer: {
    description: 'Exclusive nautical experiences in Sardinia, curated with passion to ensure maximum comfort at sea.',
    quickLinks: 'Quick Links',
    exploreBoats: 'Explore the Fleet',
    reservedArea: 'Reserved Area',
    contacts: 'Contacts',
    location: 'Sardinia, Italy',
    copyright: '© 2026 InsulaCharter.',
  },

  // ============================================
  // HOMEPAGE
  // ============================================
  home: {
    tagline: 'Sardinia • Costa Smeralda',
    welcomeTitle: 'The Sea Awaits You',
    welcomeSubtitle: 'Find the perfect boat for your adventure in the crystal-clear waters of the Costa Smeralda and the coves of Sardinia.',
    allPorts: 'All ports',
    passengersPlaceholder: 'Passengers',
    passenger1: '1 passenger',
    passengers2: '2 passengers',
    passengers3: '3 passengers',
    passengers4: '4 passengers',
    passengers5: '5 passengers',
    passengers6Plus: '6+ passengers',
    ourDestinations: 'Our Destinations',
    categoryCoastline: 'Coast',
    categoryIslands: 'Islands',
    categoryBays: 'Coves',
    categoryOpen: 'Open Sea',
    boatsInSardinia: 'Boats in Sardinia',
    noBoatsTitle: 'No boats available',
    noBoatsMessage: 'Try adjusting the search filters',
    resetSearch: 'Reset search',
    boatFound: 'boat found',
    boatsFound: 'boats found',
    noResultsTitle: 'No boats found.',
    noResultsMessage: 'Try adjusting the search criteria',
    showAllBoats: 'Show all boats',
    verifiedFleet: 'Certified and verified fleet',
    insurance: 'insurance',
    startDate: 'Departure date',
    endDate: 'Return date',
    noLicense: 'No license required',
    // Hero
    premiumSelection: 'INSULA CHARTER PREMIUM SELECTION',
    heroDescription: 'Explore the most secluded coves of Sardinia with our premium fleet. Luxury meets freedom.',
    // Benefits bar
    benefitFlexibility: 'Total Flexibility',
    benefitInstant: 'Instant Rental',
    benefitCertified: 'Certified Charters',
    // Catalog
    searchResults: 'Search Results',
    catalogTitle: 'Boat Catalog',
    searchResultsSubtitle: 'We found {count} boats matching your criteria.',
    catalogSubtitle: 'Discover the most exclusive boats selected for you.',
    resetFilters: 'Reset All Filters',
    noResults: 'Oops! No boats available for these criteria.',
    noResultsHint: 'Try adjusting the filters or broadening your search.',
    resetFiltersBtn: 'Reset Filters',
    loadingBoats: 'Loading boats...',
    loadingMap: 'Loading map...',
    // Destinations section
    exclusiveDestinations: 'The Most Exclusive Destinations',
    exclusiveDestinationsDesc: 'From Costa Smeralda to the wild coves of Ogliastra. Sail to paradise.',
    exploreMap: 'Explore the Map',
  },

  // ============================================
  // NAVBAR (extended)
  // ============================================
  navbarExt: {
    home: 'Home',
    fleet: 'Fleet',
    excursions: 'Excursions',
    destinations: 'Destinations',
    blog: 'Blog',
    vendorPanel: 'Vendor Panel',
    vendorRole: 'Vendor',
    lightTheme: 'Light theme',
    darkTheme: 'Dark theme',
    navigation: 'Navigation',
    administration: 'Administration',
    account: 'Account',
    settings: 'Settings',
    services: 'Services',
  },

  // ============================================
  // FOOTER (extended)
  // ============================================
  footerExt: {
    home: 'Home',
    destinations: 'Destinations',
    support: 'Support',
    helpCenter: 'Help Center',
    termsConditions: 'Terms & Conditions',
    cookiePrivacy: 'Cookie & Privacy',
    kaskoInsurance: 'Kasko Insurance',
    securePayments: 'Secure Payments',
  },

  // ============================================
  // SEARCH SECTION
  // ============================================
  searchSection: {
    city: 'City',
    allCities: 'All',
    allCitiesLong: 'All cities',
    noCities: 'No cities available',
    dates: 'Dates',
    dateStart: 'Start',
    dateEnd: 'End',
    times: 'Times',
    pickup: 'Pick-up',
    dropoff: 'Drop-off',
    guests: 'Guests',
    passengers: 'Passengers',
    license: 'License',
    licenseAll: 'All',
    licenseNo: 'No',
    search: 'Search',
  },

  // ============================================
  // CATALOG TOOLBAR & FILTERS
  // ============================================
  catalog: {
    // Toolbar
    filters: 'Filters',
    searchPlaceholder: 'Search boat, port, description...',
    sortPriceAsc: 'Price: low to high',
    sortPriceDesc: 'Price: high to low',
    sortRating: 'Rating',
    sortReviews: 'Most reviewed',
    sortNewest: 'Newest',
    sortName: 'Name A-Z',
    sortDefault: 'Sort by',
    viewGrid: 'Grid view',
    viewList: 'List view',
    viewMap: 'Map view',
    boatFound: 'boat found',
    boatsFound: 'boats found',
    // Filters
    boatType: 'Boat Type',
    dailyPrice: 'Daily Price',
    priceRange: 'Available range',
    passengerCapacity: 'Passenger Capacity',
    departureCity: 'Departure City',
    allCities: 'All cities',
    options: 'Options',
    noLicense: 'No boating license',
    skipperAvailable: 'Skipper available',
    resetFilters: 'Reset Filters',
    showResults: 'Show results',
    // Pagination
    showing: 'Showing',
    of: 'of',
    results: 'results',
    prevPage: 'Previous page',
    nextPage: 'Next page',
    pageOf: 'Page {current} of {total}',
  },

  // ============================================
  // BOAT CARD (extended)
  // ============================================
  boatCardExt: {
    premiere: 'Insula Première',
    licenseRequired: 'License Required',
    noLicense: 'No License',
    unavailable: 'Unavailable',
    startingFrom: 'Starting from',
    from: 'from',
    seasonalRate: 'Seasonal rate',
    perDay: '/day',
    perHalfDay: '/half-day',
    perWeek: '/week',
    perMonth: '/month',
    shortStaySurcharge: 'Short-stay surcharge',
    length: 'Length',
    passengers: 'Passengers',
    cabins: 'Cabins',
    bathrooms: 'Bathrooms',
    engine: 'Engine',
    skipperIncluded: 'Skipper Included',
    skipperAvailable: 'Skipper Available',
    viewAlternatives: 'View Alternatives',
    exploreDetails: 'Explore Details',
    alternatives: 'Alternatives',
    details: 'Details',
    license: 'License',
    noLicenseShort: 'No License',
    people: 'people',
    forDays: 'for {days} days',
    premium: 'Premium',
  },

  // ============================================
  // DATE RANGE PICKER
  // ============================================
  datePicker: {
    title: 'Select rental dates',
    clearSelection: 'Clear selection',
    confirmDates: 'Confirm Dates',
    boarding: 'Pick-up',
    disembark: 'Drop-off',
    day: 'day',
    days: 'days',
    selectBoarding: 'Select the pick-up date',
    selectDisembark: 'Select the drop-off date (click the same date for a single-day rental)',
    available: 'Available',
    notAvailable: 'Not available',
    halfDay: 'Half day',
    morningFree: 'Morning available',
    morningBooked: 'Morning booked',
    afternoonFree: 'Afternoon available',
    afternoonBooked: 'Afternoon booked',
    weekdayMon: 'Mon',
    weekdayTue: 'Tue',
    weekdayWed: 'Wed',
    weekdayThu: 'Thu',
    weekdayFri: 'Fri',
    weekdaySat: 'Sat',
    weekdaySun: 'Sun',
    weekdayFullMon: 'Monday',
    weekdayFullTue: 'Tuesday',
    weekdayFullWed: 'Wednesday',
    weekdayFullThu: 'Thursday',
    weekdayFullFri: 'Friday',
    weekdayFullSat: 'Saturday',
    weekdayFullSun: 'Sunday',
    monthJan: 'January',
    monthFeb: 'February',
    monthMar: 'March',
    monthApr: 'April',
    monthMay: 'May',
    monthJun: 'June',
    monthJul: 'July',
    monthAug: 'August',
    monthSep: 'September',
    monthOct: 'October',
    monthNov: 'November',
    monthDec: 'December',
  },

  // ============================================
  // BOAT TYPES
  // ============================================
  boatTypes: {
    yacht: 'Yacht',
    sailboat: 'Sailboat',
    motorboat: 'Motorboat',
    catamaran: 'Catamaran',
    dinghy: 'Dinghy',
    jetski: 'Jet Ski',
  },

  // ============================================
  // BOAT CARD
  // ============================================
  boatCard: {
    exclusive: 'Premium',
    pricePerDay: 'Daily price',
    licenseRequired: 'License required',
    noLicenseRequired: 'No license required',
    capacity: 'Capacity',
    length: 'Length',
    power: 'Power',
  },

  // ============================================
  // BOAT (New fields)
  // ============================================
  boat: {
    linkCopied: 'Link copied to clipboard!',
    port: 'Departure Port',
    enginePower: 'Engine Power',
    skipperAvailable: 'Skipper Available',
    skipperIncluded: 'Skipper Included',
    hostessAvailable: 'Hostess Available',
    noLicense: 'No License Required',
    licenseRequired: 'License Required',
    amenities: 'Onboard Equipment',
    extras: 'Extra Services',
    perStay: 'per rental',
    perDay: 'per day',
    perPerson: 'per person',
    perPersonPerDay: 'per person per day',
    coverImage: 'Cover Photo',
    selectCover: 'Set as cover',
    freeService: 'Free',
    externalServicesTitle: 'Services available at this port',
    externalServicesEmpty: 'No external services available at this port.',
    externalServicesLoading: 'Loading services...',
    externalServicesPlaceholder: 'This boat offers additional services',
    externalServicesContactUs: 'Contact us for more information about the available services.',
    availableServices: 'Curated services for this boat',
    fromPrice: 'from €{price}',
    nProvidersAvailable: '{count} providers available',
    anonymousProvider: 'Provider',
    externalServiceCategoryMobility: 'Rental / Mobility',
    externalServiceCategoryOther: 'Service',
    requestQuote: 'Request a quote',
    requestQuoteTitle: 'Request a quote',
    requestQuoteDate: 'Preferred date',
    requestQuoteGuests: 'Number of people',
    requestQuoteNotes: 'Notes (optional)',
    requestQuoteName: 'Your name',
    requestQuoteEmail: 'Email',
    requestQuotePhone: 'Phone',
    requestQuoteSubmit: 'Send request',
    requestQuoteSuccess: 'Request sent. The provider will contact you shortly.',
    requestQuoteAuthHint: 'Leave your contact details so the provider can reply',
  },

  // ============================================
  // REVIEWS (Reviews)
  // ============================================
  reviews: {
    title: 'Review Management',
    newReview: 'New Review',
    editReview: 'Edit Review',
    noReviews: 'No reviews',
    addFirst: 'Add the first review for your boats',
    authorName: 'Author Name',
    rating: 'Rating',
    comment: 'Comment',
    reviewDate: 'Review Date',
    adminResponse: 'Admin Response',
    isVisible: 'Review visible to the public',
    hidden: 'Hidden',
    stars: 'stars',
    star: 'star',
    averageRating: 'Average',
    totalReviews: 'reviews',
    selectBoat: 'Select a boat',
    deleteConfirm: 'Do you want to delete this review?',
    created: 'Review created successfully',
    updated: 'Review updated successfully',
    deleted: 'Review deleted successfully',
    showReview: 'Show',
    hideReview: 'Hide',
  },

  // ============================================
  // BOAT DETAIL
  // ============================================
  boatDetail: {
    backButton: 'Explore the Fleet',
    notFound: 'Boat not found.',
    loginRequired: 'Log in to book.',
    selectDates: 'Select dates from the search.',
    characteristics: 'Characteristics',
    description: 'Description',
    tagSkipper: 'Skipper Available',
    tagInsured: 'Insurance Included',
    tagSupport: '24/7 Support',
    whereIsIt: 'Departure port',
    locationDescription: 'Located in the port of {location}, one of the most exclusive in Sardinia.',
    excellentPosition: 'Strategic Location',
    homePort: 'Home Port',
    rate: 'Rate',
    rental: 'Rental',
    passengersLabel: 'Passengers',
    extrasOptional: 'Extra Services (Optional)',
    fuelPolicy: 'Fuel',
    extrasTotal: 'Extra services',
    bookNow: 'BOOK NOW',
    processing: 'PROCESSING...',
    protectionBadge: 'InsulaCharter Protection Guaranteed',
    paymentMethod: 'Payment Method',
    selectPaymentMethod: 'Select a payment method',
    noPaymentMethods: 'No saved payment methods',
    addPaymentMethod: 'Add a payment method',
    cashDescription: 'Pay cash upon pickup',
    guaranteeRequired: 'Security deposit required',
    guaranteeAmount: 'Deposit amount',
    selectGuaranteeCard: 'Select the card for the deposit',
    cashNoCard: 'Pay cash (admin confirmation required)',
    cashNoCardDescription: 'Without a guarantee card, the booking requires manual confirmation',
    specifications: 'Technical Specifications',
    length: 'Length',
    beam: 'Beam',
    engine: 'Engine',
    fuelCapacity: 'Fuel Tank',
    maxSpeed: 'Max Speed',
  },

  // ============================================
  // DASHBOARD (GUEST)
  // ============================================
  dashboard: {
    title: 'Personal Area',
    greeting: 'Welcome back, {name}. Manage your rentals in Sardinia.',
    profileSailor: 'Sailor Profile',
    menuUpcomingTrips: 'Upcoming Rentals',
    menuPaymentMethods: 'Payment Methods',
    menuSaved: 'Saved',
    menuPersonalInfo: 'Personal Info',
    confirmedBookings: 'Confirmed Bookings',
    pickupDate: 'Pickup',
    returnDate: 'Return',
    receipt: 'Receipt',
    cancelBooking: 'Cancel',
    noTripsTitle: 'No confirmed rentals',
    noTripsMessage: 'The sea awaits you. Start planning your next adventure!',
    exploreBoats: 'Explore the Fleet',
    historyAndCancelled: 'History and Cancelled',
    statusCancelled: 'Cancelled',
    myBookings: 'My Bookings',
    upcomingTrips: 'Upcoming Rentals',
    paymentMethods: 'Payment Methods',
    saved: 'Saved',
    personalInfo: 'Personal Info',
    noBookings: 'No rentals booked',
    noBookingsDesc: 'The sea awaits you. Start planning your next adventure!',
    savedComingSoon: 'Saved Boats',
    savedDescription: 'Feature coming soon. You will soon be able to save your favorite boats.',
  },

  // ============================================
  // ADMIN DASHBOARD
  // ============================================
  admin: {
    title: 'Admin Dashboard',
    subtitle: 'Centralized management of InsulaCharter Sardinia.',
    addBoat: 'Add Boat',
    bookingsTitle: 'Bookings',
    bookingsSubtitle: 'Manage bookings',

    // Boat port/marina (for external services matching)
    boatPortMarina: 'Port/Marina',
    boatPortMarinaHint: 'External services visible to customers depend on the port selected here.',
    boatPortMarinaNone: '— No port —',
    boatPortMarinaLoading: 'Loading ports...',

    // Role management
    changeRole: 'Change Role',
    changeRoleConfirm: 'Are you sure you want to change this user\'s role?',
    changeRoleSuccess: 'Role updated successfully',
    changeRoleAdminWarning: 'This grants full admin access to the platform.',
    changeRoleWarningBoats: 'This user has {count} active boats. Changing the role will deactivate their listings.',
    changeRoleForce: 'Proceed anyway',
    cannotChangeOwnRole: 'You cannot change your own role',
    cannotDemoteLastAdmin: 'Cannot remove the last platform administrator',
    roleAlreadyAssigned: 'User already has this role',
    confirmAdminRequired: 'Explicit confirmation required to promote to Admin',
    invalidRole: 'Invalid role',
    role_hub_internal_only: 'This account (ADMIN or Service Provider) is managed only from the hub, not here.',
    newRole: 'New role',
    vendorStatusOption: 'Initial vendor status',

    // Service Provider invitations
    inviteServiceProvider: 'Invite Provider',
    inviteEmailLabel: 'Provider email',
    inviteNameLabel: 'Name (optional)',
    inviteDesc: 'An email will be sent with a registration link valid for 48 hours.',
    inviteSend: 'Send Invitation',
    inviteSent: 'Invitation sent successfully',
    inviteAcceptTitle: 'Complete Registration',
    inviteAcceptDesc: 'You have been invited to join InsulaCharter as a Service Provider.',

    // Preview links
    previewVendor: 'Preview Vendor',
    previewServiceProvider: 'Preview Provider',

    // Tabs
    tabDashboard: 'Overview',
    tabCalendar: 'Calendar',
    tabMyBoats: 'Fleet',
    tabReviews: 'Reviews',
    tabBookings: 'Boat bookings',
    tabClientBookings: 'Client Bookings',
    tabServices: 'Onboard Services',
    calendarTitle: 'Booking Calendar',
    calendarSubtitle: 'View all bookings for your fleet',
    fleetOverview: 'Fleet Overview',

    // Stats
    statsOverview: 'General Overview',
    statsTotalRevenue: 'Total Revenue',
    statsConfirmed: 'confirmed',
    statsUniqueClients: 'Unique Clients',
    statsRegisteredClients: 'registered clients',
    statsActiveBoats: 'Active Boats',
    statsInFleet: 'in the fleet',
    statsPending: 'Pending',
    statsToConfirm: 'to confirm',

    // Economic averages
    economicAverages: 'Economic Averages',
    avgBookingValue: 'Average Booking Value',
    avgPricePerDay: 'Average Daily Price',
    confirmationRate: 'Confirmation Rate',

    // Booking trends
    bookingTrends: 'Booking Trends',
    last30Days: 'Last 30 Days',
    confirmedBookings: 'confirmed bookings',
    bookingStatus: 'Booking Status',
    statusConfirmed: 'Confirmed',
    statusPending: 'Pending',
    statusCancelled: 'Cancelled',

    // Pricing tabs
    tabPricingRules: 'Pricing Rules',
    tabPricingCalendar: 'Pricing Calendar',
    tabTariffe: 'Rates',
    tabTariffeReadonly: 'Rates (read-only)',
    tabPricing: 'Pricing & Promos',

    // Pricing management (3-step flow)
    pricingTitle: 'Pricing',
    pricingSubtitle: 'Browse owners, boats and detailed pricing',
    pricingBreadcrumbOwners: 'All owners',
    pricingSelectOwner: 'Select an owner',
    pricingLoadingOwners: 'Loading owners...',
    pricingNoOwnersFound: 'No owners found',
    pricingFilterAll: 'All',
    pricingSearchOwner: 'Search name or email...',
    pricingColOwner: 'Owner',
    pricingColEmail: 'Email',
    pricingColRole: 'Role',
    pricingColBoats: 'Boats',
    pricingBoatsOf: 'Boats of',
    pricingBoatSingular: 'boat',
    pricingBoatPlural: 'boats',
    pricingNoBoats: 'This owner has no registered boats',
    pricingColBoatName: 'Boat',
    pricingColType: 'Type',
    pricingColLength: 'Length',
    pricingColPriceDay: '\u20AC/day',
    pricingColPriceHalf: '\u20AC/half',
    pricingColStatus: 'Status',
    pricingBackToOwners: 'Back to owners',
    pricingBackToBoats: 'Back to boats of',
    pricingLoadingPricing: 'Loading pricing...',
    pricingSearchBoats: 'Search boat...',
    pricingAllTypes: 'All types',
    pricingAllStatuses: 'All statuses',

    tabPricePolicies: 'Price Policies',
    tabCoupons: 'Coupons & Promotions',
    tabInviteCodes: 'Invite Codes',
    tabAmbassadors: 'Ambassadors',
    tabAmbassadorTiers: 'Ambassador Tiers',
    tabServicePlatformFee: 'Service Fees',
    tabExternalServicesPayout: 'Service Payouts',
    usersRetrievalError: 'Error retrieving users',

    // Fleet management
    fleet: {
      title: 'Fleet Management',
      addBoat: 'Add Boat',
      filterAll: 'All',
      filterCompany: 'Companies',
      filterVendor: 'Private',
      filterPlatform: 'Platform',
      searchPlaceholder: 'Search boat, owner, port...',
      allTypes: 'All types',
      allPorts: 'All ports',
      clearFilters: 'Clear filters',
      expandAll: 'Expand all',
      collapseAll: 'Collapse all',
      boat: 'boat',
      boats: 'boats',
      owner: 'owner',
      owners: 'owners',
      company: 'Company',
      vendor: 'Private Vendor',
      platform: 'Platform',
      colBoat: 'Boat',
      colType: 'Type',
      colPort: 'Port',
      colCapacity: 'Capacity',
      colPrice: 'Rate/Day',
      colActions: 'Actions',
      rentalDay: 'day',
      rentalWeek: 'week',
      rentalMonth: 'month',
      viewDetails: 'View details',
      edit: 'Edit',
      delete: 'Delete',
      empty: 'No boats in the fleet',
      emptyHint: 'Start by adding your first boat',
      noResults: 'No boats found',
      noResultsHint: 'Try adjusting your search filters',
    },

    // Promotions tab
    tabPromotions: 'Promotions',
    tabSuspensions: 'Suspensions',
    tabMobility: 'External Services',
    tabMobilityServices: 'Service Catalog',
    tabExtraCategories: 'Service Categories',
    tabExternalServices: 'External Services (API)',
    apiCatalogTitle: 'Service Catalog from InsulaServices',
    apiCatalogDesc: 'Services available from insulaservices.it for boats. Click "Sync" to refresh.',
    apiCatalogSync: 'Sync',
    apiCatalogSyncing: 'Syncing...',
    apiCatalogEmpty: 'No services available. Try syncing.',
    apiCatalogError: 'Error contacting insulaservices.it',
    apiCatalogQuote: 'On request',
    apiCatalogPerPerson: '/person',
    extraCategoriesTitle: 'Service Categories',
    extraCategoriesDesc: 'Create and manage service categories. Categories marked "external" are visible to clients and selectable by Service Providers.',
    extraCategoriesNew: 'New category',
    extraCategoriesEdit: 'Edit category',
    extraCategoriesEmpty: 'No categories configured',
    extraCategoriesSlug: 'Slug (unique ID)',
    extraCategoriesLabel: 'Label',
    extraCategoriesDescription: 'Description',
    extraCategoriesIcon: 'Icon',
    extraCategoriesIconHint: 'Lucide icon name (e.g. Bike, Camera, Utensils, Anchor)',
    extraCategoriesColor: 'Badge color',
    extraCategoriesSortOrder: 'Sort order',
    extraCategoriesVisibility: 'Visibility',
    extraCategoriesExternal: 'External (visible to clients)',
    extraCategoriesExternalShort: 'external',
    extraCategoriesInternal: 'Internal (admin only)',
    extraCategoriesInternalShort: 'internal',
    extraCategoriesProtected: 'Protected category — cannot be deleted',
    extraCategoriesProtectedHint: 'Protected categories cannot change slug or visibility',
    extraCategoriesUsage: 'in use',
    extraCategoriesMoveUp: 'Move up',
    extraCategoriesMoveDown: 'Move down',
    extraCategoriesValidation: 'Slug and label are required',
    extraCategoriesCreated: 'Category created',
    extraCategoriesUpdated: 'Category updated',
    extraCategoriesDeleted: 'Category deleted',
    extraCategoriesConfirmDelete: 'Permanently delete this category?',
    mobilityServices: 'Mobility Services',
    mobilityServicesDesc: 'Manage bikes, scooters, kick scooters and transfers available for guests',
    noMobilityServices: 'No mobility services configured',
    tabTransferOverview: 'Transfers',
    transferOverview: 'Transfer Overview',
    unassigned: 'Unassigned',
    reassign: 'Reassign',
    tabServiceProviders: 'Service Providers',
    tabServiceRequests: 'Service Requests',
    serviceProviders: 'Service Providers',
    serviceProvidersDesc: 'Manage providers that deliver mobility and transfer services',
    addProvider: 'Add Provider',
    noProviders: 'No providers registered',
    selectUser: 'Select User',
    selectService: 'Select Service',
    maxCapacity: 'Max Capacity',
    serviceRequests: 'Service Requests',
    serviceRequestsDesc: 'Overview of all service requests from bookings',
    noServiceRequests: 'No service requests',
    // Mobility sub-tab extras
    tabMobilityOverview: 'Overview',
    mobilityOverview: 'Mobility Overview',
    mobilityOverviewDesc: 'Global stats for providers and service requests',
    mobilityProvidersTotal: 'Total providers',
    mobilityProvidersActive: 'Active providers',
    mobilityProvidersInactive: 'Inactive providers',
    mobilityRequestsTotal: 'Total requests',
    mobilityTopProviders: 'Top 5 providers (completed requests)',
    mobilityNoTopProviders: 'No provider has completed requests yet',
    mobilityRefresh: 'Refresh',
    mobilityLoadError: 'Failed to load mobility data',
    mobilityOpenSpDashboard: 'Open SP dashboard',
    mobilityPendingBadge: '{count} pending',
    mobilityStatusActive: 'Active',
    mobilityStatusInactive: 'Inactive',
    mobilityEditName: 'Name',
    mobilityEditPrice: 'Price (€)',
    mobilityEditPriceType: 'Price type',
    mobilityEditDescription: 'Description',
    mobilityEnable: 'Enable',
    mobilityDisable: 'Disable',
    mobilityLabelCapacity: 'Cap.',
    mobilityLabelProvider: 'Provider',
    mobilityLabelQuantity: 'Qty',
    mobilityToggleError: 'Unable to update service status',
    mobilitySaveError: 'Unable to save changes',
    mobilityLoadProvidersError: 'Unable to load providers',
    mobilityLoadRequestsError: 'Unable to load requests',
    // External services moderation
    externalServicesTitle: 'External Services',
    externalServicesDesc: 'Moderate and manage services created by Service Providers',
    externalServicesPending: 'Pending approval',
    externalServicesApprove: 'Approve',
    externalServicesReject: 'Reject',
    externalServicesRejectReason: 'Rejection reason',
    externalServicesNotes: 'Notes (visible to SP)',
    externalServiceCreator: 'Created by',
    externalServiceCreatorAdmin: 'Admin (platform)',
    externalServicePorts: 'Operating ports',
    externalServiceNoPort: 'No port linked',
    // Price type labels
    priceType_per_stay: 'Per stay',
    priceType_per_day: 'Per day',
    priceType_per_night: 'Per night',
    priceType_per_person: 'Per person',
    priceType_per_person_per_day: 'Per person/day',
    priceType_per_person_per_night: 'Per person/night',
    confirmDelete: 'Are you sure you want to delete?',
    tabPoi: 'Points of Interest',
    tabPorts: 'Ports',
    promoTitle: 'Title',
    promoSubtitle: 'Subtitle',
    promoDescription: 'Description',
    promoImage: 'Image',
    promoCtaText: 'CTA Text',
    promoCtaLink: 'CTA Link',
    promoBadge: 'Badge',
    promoStartDate: 'Start Date',
    promoEndDate: 'End Date',
    promoActive: 'Active',
    promoOrder: 'Order',
    promoNew: 'New Promotion',
    promoEdit: 'Edit Promotion',
    promoDelete: 'Delete Promotion',
    promoDeleteConfirm: 'Are you sure you want to delete this promotion?',
    promoCreated: 'Promotion created successfully',
    promoUpdated: 'Promotion updated successfully',
    promoDeleted: 'Promotion deleted successfully',
    promoNoResults: 'No promotions found',
    promoAlwaysVisible: 'Always visible',
    promoSave: 'Save',
    promoCancel: 'Cancel',
    promoUploadImage: 'Upload image',
    promoOrImageUrl: 'or enter image URL',
    promoToggleActive: 'Toggle Active',
    promoReorderHint: 'Use arrows to reorder',
    promoMoveUp: 'Move up',
    promoMoveDown: 'Move down',

    // Trends tab
    tabTrends: 'Trends',
    trendsTotalRevenue: 'Period Revenue',
    trendsMonthlyAvg: 'Monthly Average',
    trendsPerMonth: 'per month',
    trendsTrend: 'Trend',
    trendsVsPrev: 'vs previous period',
    trendsBookings: 'Bookings',
    trendsRevenue: 'Revenue',

    // Recent bookings table
    recentBookings: 'Recent Bookings',
    tableDate: 'Date',
    tableBoat: 'Boat',
    tablePickupReturn: 'Pickup/Return',
    tableValue: 'Value',
    tableStatus: 'Status',
    noBookingsYet: 'No bookings yet',
    clientProfile: 'Client Profile',
    bookingHistory: 'Booking History',
    totalSpent: 'Total Spent',
    cancellationReason: 'Cancellation reason',
    confirmBooking: 'Confirm booking',
    cancelBooking: 'Cancel booking',
    completeBooking: 'Complete booking',
    refundDeposit: 'Refund deposit',
    depositRefunded: 'Deposit refunded',

    // Boats table
    tableVessel: 'Vessel',
    tablePort: 'Port',
    tableRate: 'Rate',
    tableActions: 'Actions',

    // Bookings table
    tableClients: 'Clients',
    tablePeriod: 'Period',
    tableTotal: 'Total',
    tablePaymentStatus: 'Payment',
    vendorSuspended: 'Vendor suspended',
    vendorBookingsSuspended: 'Bookings suspended',
    boatName: 'Boat',
    email: 'Email',
    embarkation: 'Embarkation',
    disembarkation: 'Disembarkation',
    deposit: 'Deposit',

    // Booking list/calendar view
    allStatuses: 'All',
    allPaymentStatuses: 'All payments',
    paymentPending: 'Unpaid',
    paymentPaid: 'Paid',
    paymentPartial: 'Partial',
    paymentRefunded: 'Refunded',
    calendarView: 'Calendar',
    listView: 'List',
    searchGuest: 'Search client...',
    cancelWithReason: 'Cancel with reason',
    cancelConfirmMessage: 'Are you sure you want to cancel this booking?',
    cancelReasonPlaceholder: 'Cancellation reason (optional)',

    // Summary cards
    pendingBookings: 'Pending',
    confirmedRevenue: 'Confirmed Revenue',

    // Booking detail modal
    bookingDetail: 'Booking Detail',
    guestInfo: 'Client Information',
    firstName: 'Name',
    boatInfo: 'Boat Information',
    port: 'Port',
    rentalDetails: 'Rental Details',
    experienceDetails: 'Experience details',
    priceSummary: 'Price Summary',
    basePrice: 'Base price',
    insuranceFee: 'Insurance',
    extras: 'Extras',
    specialRequests: 'Special Requests',
    downloadReceipt: 'Receipt',
    noBookingsFound: 'No bookings found',

    // Reversible bookings
    reversibleBookings: 'Reversible Bookings',
    reversibleBookingsSubtitle: 'You can restore the previous status',
    reverse: 'Restore',
    undo: 'Undo',

    // Calendar overview
    boatsOverview: 'Boats Overview',
    confirmedShort: 'confirmed',
    pendingShort: 'pending',
    blockedShort: 'blocked',

    // Services section
    includedServices: 'Onboard Equipment',
    includedServicesSubtitle: 'Manage the equipment included on the boats',
    addEquipment: 'Add Equipment',
    extraServices: 'Extra Services (Paid)',
    extraServicesSubtitle: 'Manage extra services that can be added to rentals. The price is set for each boat.',
    addExtraService: 'Add Extra Service',

    // Extras approval (admin)
    extraFilterAll: 'All',
    extraFilterPlatform: 'Platform',
    extraFilterVendors: 'Vendor',
    extraFilterPending: 'Pending',
    extraCreatorPlatform: 'Platform',
    extraCreatorLabel: 'Created by',
    extraApprove: 'Approve',
    extraReject: 'Reject',
    extraRejectNotes: 'Rejection reason',
    extraRejectNotesPlaceholder: 'Explain the reason for rejection...',
    extraPendingCount: 'extras pending',
    extraApproved: 'Extra approved',
    extraRejected: 'Extra rejected',
    extraApproveError: 'Error approving',
    extraRejectError: 'Error rejecting',
    extraPromote: 'Make global',
    extraPromoted: 'Extra promoted to platform-wide global service',
    extraPromoteError: 'Error promoting',
    extraPromoteConfirm: 'Make this extra available for all boats on the platform? The vendor will lose exclusive ownership.',

    // Audit History
    auditTitle: 'Operations History',
    auditSubtitle: 'Complete history of extra service operations for traceability and dispute resolution',
    auditTotalEntries: 'recorded operations',
    auditNoEntries: 'No recorded operations',
    auditDetails: 'Operation details',
    auditPage: 'Page',

    tableService: 'Service',
    tableDescription: 'Description',
    tableIcon: 'Icon',

    // Boat modal
    modalNewBoat: 'New InsulaCharter Boat',
    modalEditBoat: 'Edit InsulaCharter Boat',
    formBoatName: 'Boat Name',
    formBoatNamePlaceholder: 'e.g.: Azzurra',
    formPort: 'Home Port',
    formPortPlaceholder: 'e.g.: Porto Cervo',
    formPricePerDay: 'Daily Price (€)',
    formInsuranceFee: 'Insurance (€)',
    formDescription: 'Description',
    formDescriptionPlaceholder: 'Describe the characteristics of this boat...',

    // Photos section
    photosSection: 'Boat Photos',
    photoCover: 'Cover',
    photoSetCover: 'Set as Cover',
    addPhoto: 'Add Photo',
    addPhotoMaxReached: 'Add Photo (Maximum reached)',

    // Specifications
    specifications: 'Technical Specifications',
    formLength: 'Length (m)',
    formBeam: 'Beam (m)',
    formCapacity: 'Passenger Capacity',
    formEngine: 'Engine',
    formLicenseRequired: 'Requires Boating License',

    // Services in boat
    onboardEquipment: 'Onboard Equipment',
    extraServicesPaid: 'Extra Services (paid)',
    extraServicesCustomPrice: 'Set custom prices for this boat',

    // Buttons
    saveChanges: 'Save Changes',
    createListing: 'Publish Boat',

    // Equipment modal
    modalNewEquipment: 'New Equipment',
    modalEditEquipment: 'Edit Equipment',
    formServiceId: 'Service ID',
    formServiceIdPlaceholder: 'e.g.: bimini_top',
    formServiceIdHelper: 'Use only lowercase letters and underscores',
    formServiceName: 'Service Name',
    formServiceNamePlaceholder: 'e.g.: Bimini Top',
    formServiceDescriptionPlaceholder: 'Describe the equipment...',
    formIconOptional: 'Icon (optional)',
    iconDefault: 'Info (default)',
    iconAnchor: 'Anchor',
    iconLifeBuoy: 'Life Buoy',
    iconCompass: 'Compass',
    iconSun: 'Sun',
    iconWaves: 'Waves',
    iconWind: 'Wind',
    iconFuel: 'Fuel',
    iconRadio: 'Radio',
    iconGps: 'GPS',

    // Extra modal
    modalNewExtra: 'New Extra Service',
    modalEditExtra: 'Edit Extra Service',
    formExtraIdPlaceholder: 'e.g.: skipper_service',
    formExtraNamePlaceholder: 'e.g.: Professional Skipper',
    formExtraDescriptionPlaceholder: 'Describe the extra service...',
    extraPriceInfo: 'The service price will be set individually for each boat.',

    // Multilingual translations
    translationsSection: 'Translations',
    translationsHelper: 'Enter translations for each supported language',
    translationsHelperAuto: 'Enter the text in Italian and click "Auto Translate" to generate the other translations.',
    autoTranslate: 'Auto Translate',
    translating: 'Translating...',
    translationComplete: 'Translation completed!',
    translationFailed: 'Translation error',
    enterItalianFirst: 'Enter the Italian text first',
    sourceLang: 'Source language',
    labelIT: 'Name (Italian)',
    labelEN: 'Name (English)',
    labelDE: 'Name (German)',
    descriptionIT: 'Description (Italian)',
    descriptionEN: 'Description (English)',
    descriptionDE: 'Description (German)',
    langIT: 'Italian',
    langEN: 'English',
    langDE: 'German',

    // Category options
    formCategory: 'Category',
    categoryGeneral: 'General service',
    categorySafety: 'Safety',
    categoryComfort: 'Comfort',
    categoryNavigation: 'Navigation',
    categoryEntertainment: 'Entertainment',
    categorySpecial: 'Special service (green badge)',

    // Blocked Dates
    allBoats: 'All boats',
    blockDates: 'Block Dates',
    blockDatesTitle: 'Block Dates',
    blockedPeriods: 'Blocked Periods',
    blocked: 'blocked',
    unblock: 'Unblock',
    blockingFor: 'Blocking dates for',
    startDate: 'Start date',
    endDate: 'End date',
    blockReason: 'Reason (optional)',
    blockReasonPlaceholder: 'e.g.: Maintenance, Personal use...',
    blockWarning: 'Blocked dates will not be available for new rentals and will be shown as "unavailable" to users.',
    confirmBlock: 'Block Dates',
    selectBoatFirst: 'Select a boat first',
    selectDatesFirst: 'Select the dates',
    datesBlocked: 'Dates blocked successfully',
    datesUnblocked: 'Dates unblocked',
    confirmUnblock: 'Do you want to unblock these dates?',
    tabSettings: 'Settings',
    tabClients: 'Clients',
    tabClientManagement: 'Clients',

    // Clients section
    clientsTitle: 'Client Management',
    searchClients: 'Search by name or email...',
    searchBookings: 'Search client, email...',
    noClients: 'No registered clients',
    tableClient: 'Client',
    tableEmail: 'Email',
    tableBookingsCount: 'Bookings',
    tableTotalSpent: 'Total Spent',
    tableRegistered: 'Registered on',
    registeredOn: 'Registered on',
    totalBookings: 'Total',
    viewClient: 'View Client',
    clientConfirmedBookings: 'Confirmed',
    clientPendingBookings: 'Pending',
    clientCancelledBookings: 'Cancelled',
    emailVerified: 'Email Verified',
    emailNotVerified: 'Email Not Verified',

    // Admin User Management
    userManagement: 'Advanced Management',
    userManagementDesc: 'Administrative actions for this user',
    resetEmailVerification: 'Reset Email Verification',
    resetEmailVerificationDesc: 'Reset the email verification status. The user will need to verify their email again.',
    resetEmailVerificationSuccess: 'Email verification reset successfully',
    resetPassword: 'Reset Password',
    resetPasswordDesc: 'Send a password reset email to the user.',
    resetPasswordSuccess: 'Password reset email sent',
    resetPasswordEmailFailed: 'Unable to send email. Token generated for manual use.',
    emailNotConfigured: 'Email not configured. Token generated for manual use.',
    userNotFound: 'User not found',
    confirmAction: 'Confirm Action',
    confirmResetEmailVerification: 'Are you sure you want to reset the email verification for this user?',
    confirmResetPassword: 'Are you sure you want to send a password reset email to this user?',
    // Suspension
    suspendUser: 'Suspend User',
    suspendUserDesc: 'Block access to the platform and prevent new bookings.',
    unsuspendUser: 'Reactivate User',
    unsuspendUserDesc: 'Restore full access to the platform.',
    suspendUserSuccess: 'User suspended successfully',
    unsuspendUserSuccess: 'User reactivated successfully',
    confirmSuspendUser: 'Are you sure you want to suspend this user? They will no longer be able to access the platform.',
    confirmUnsuspendUser: 'Are you sure you want to reactivate this user?',
    suspensionReason: 'Suspension reason',
    suspensionReasonPlaceholder: 'E.g.: Terms of service violation, fraudulent payments...',
    suspended: 'Suspended',
    suspendedSince: 'Suspended since',
    accountSuspended: 'Account Suspended',
    accountSuspendedDesc: 'Your account has been suspended. Contact support for more information.',
    suspendCompanyDesc: 'Block access to the platform, prevent new bookings and hide boats from the catalog.',

    // Monitoring / Logs
    tabMonitoring: 'Monitoring',
    tabLogs: 'Event Logs',
    tabRawConsole: 'Raw console',
    logs: {
      title: 'System Logs',
      description: 'Platform event and activity log',
      filterCategory: 'Category',
      filterLevel: 'Level',
      filterFrom: 'From',
      filterTo: 'To',
      filterSearch: 'Search in message...',
      filterActor: 'User (ID)',
      reset: 'Reset',
      exportCsv: 'Export CSV',
      columns: {
        timestamp: 'Date',
        level: 'Level',
        category: 'Category',
        action: 'Action',
        actor: 'User',
        target: 'Target',
        message: 'Message',
        ip: 'IP',
        details: 'Details',
      },
      detailsTitle: 'Event details',
      metadata: 'Metadata',
      requestId: 'Request ID',
      empty: 'No events',
      loading: 'Loading logs...',
      page: 'Page {{n}} of {{total}}',
      levels: {
        all: 'All',
        info: 'Info',
        warn: 'Warning',
        error: 'Error',
      },
      categories: {
        all: 'All',
        auth: 'Authentication',
        ambassador: 'Ambassador',
        booking: 'Bookings',
        payment: 'Payments',
        admin: 'Admin',
        system: 'System',
        http: 'HTTP',
      },
    },

    bookings: {
      actionCancel: 'Cancel',
      actionHardDelete: 'Delete permanently',
      paymentMethod: 'Payment method',
      amountPaid: 'Amount paid',
      confirmCancel: 'Confirm cancellation',

      hardDeleteTitle: 'Permanent deletion',
      hardDeleteWarningTitle: 'PERMANENT DELETION — IRREVERSIBLE',
      hardDeleteWarning: 'This operation deletes the booking from the database forever. Use only for test data, never in production.',
      hardDeleteConfirmType: 'To confirm, type "{keyword}"',
      hardDeleteConfirm: 'Delete permanently',

      cancelRefundTitle: 'Cancel booking & refund',
      refundPreview: 'Refund amount',
      refundPolicy: 'Applied policy',
      refundViaMollie: 'Refund automatically via Mollie',
      refundViaMollieDesc: 'Mollie processes the refund on the same card used by the customer.',
      refundManualWarningTitle: 'Manual refund required',
      refundManualWarning: 'Payment via {gateway}: after cancellation you must manually mark the refund as completed from the booking detail.',
      refundPendingBox: 'Manual refund required',
      markRefunded: 'Mark as refunded',
      waiveRefund: 'Waive refund',
      refundNote: 'Note (optional)',
      refundNotePlaceholder: 'Cancellation reason or internal note...',

      statusRefundPending: 'Refund pending',
      statusRefundProcessing: 'Refund processing',
      statusRefundFailed: 'Refund failed',
      statusRefundWaived: 'Refund waived',

      toastCancelSuccess: 'Booking cancelled',
      toastRefundedMollie: 'Refunded via Mollie',
      toastRefundPending: 'Cancelled. Manual refund pending',
      toastRefundProcessing: 'Cancelled. Refund processing',
      toastRefundFailed: 'Mollie refund failed — retry manually',
      toastDeleted: 'Booking permanently deleted',
      toastMarkedRefunded: 'Booking marked as refunded',
      toastRefundWaived: 'Refund waiver recorded',

      forceRefundTitle: 'Mollie refund not recorded',
      forceRefundDesc: 'This booking was cancelled but the Mollie refund was never executed. You can force it manually now.',
      forceRefundPaid: 'Amount paid',
      forceRefundAmount: 'Amount to refund',
      forceRefundBtn: 'Force Mollie refund',
      forceRefundConfirm: 'Confirm a refund of €{amount} on Mollie?',
      forceRefundSuccess: 'Refund processed on Mollie (€{amount})',
      forceRefundError: 'Refund failed',
      forceRefundMollieId: 'Mollie payment ID',
    },

    settings: {
      securityTitle: 'Security & Testing',
      securityDesc: 'Destructive operations and testing-only features',
      hardDeleteToggle: 'Allow permanent booking deletion',
      hardDeleteDescription: 'Test environments only. If enabled, admins can permanently delete bookings from the database. Leave OFF in production.',
      hardDeleteOn: 'ON',
      hardDeleteOff: 'OFF',
      hardDeleteWarningProd: 'WARNING: this option must remain DISABLED in production. Deletions are irreversible and remove historical data of payments, receipts and audit logs.',
    },
  },

  // ============================================
  // SETTINGS
  // ============================================
  settings: {
    title: 'Configuration',
    subtitle: 'Manage system settings',
    saved: 'Setting saved',
    invalidDays: 'Enter a value between 1 and 90',

    // Dashboard Layout
    dashboardLayout: 'Dashboard Layout',
    dashboardLayoutDesc: 'Choose how to group tabs in the admin and vendor dashboards',
    layoutAdmin: 'Admin Dashboard',
    layoutVendor: 'Vendor Dashboard',
    layoutA: 'Compact',
    layoutADesc: 'Group tabs into a few macro sections',
    layoutB: 'Operational priority',
    layoutBDesc: 'Balance between visibility and grouping',
    layoutC: 'Ultra-compact',
    layoutCDesc: 'Minimum number of main tabs',
    layoutTabs: 'tabs',
    layoutCurrent: 'Current',

    // Admin tab group labels
    layoutAdminFleet: 'Fleet',
    layoutAdminOperations: 'Operations',
    layoutAdminClientsServices: 'Clients & Services',
    layoutAdminMarketplace: 'Marketplace',
    layoutAdminFinance: 'Finance',
    layoutAdminManagement: 'Management',

    // Vendor tab group labels
    layoutVendorFleet: 'Fleet',
    layoutVendorOperations: 'Operations',
    layoutVendorServicesClients: 'Services & Clients',
    layoutVendorManagement: 'Management',

    // Sub-tab labels
    subtabStats: 'Statistics',
    subtabVendors: 'Vendors',
    subtabBoatApproval: 'Boat Approval',
    subtabRevenue: 'Revenue',
    subtabPayouts: 'Payouts',
    subtabPlatformStats: 'Platform',
    autoCancellation: 'Automatic Cancellation of Pending Bookings',
    autoCancellationDesc: 'When enabled, bookings in "Pending" status will be automatically cancelled after the specified number of days.',
    enableAutoCancel: 'Enable automatic cancellation',
    autoCancelEnabled: 'Expired pending bookings will be automatically cancelled',
    autoCancelDisabled: 'Pending bookings will not be automatically cancelled',
    daysBeforeCancel: 'Days before cancellation',
    daysBeforeCancelDesc: 'Number of days after which a pending booking is cancelled (1-90)',
    howItWorks: 'How it works',
    howItWorksDesc: 'The system checks every day at midnight for bookings in "Pending" (PENDING) status. If a booking was created more than the specified number of days ago and has not yet been confirmed, it is automatically cancelled with the reason "Automatic expiration".',

  },

  // ============================================
  // AUTH - LOGIN
  // ============================================
  login: {
    title: 'Welcome Back',
    subtitle: 'Log in to your account',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    submitButton: 'Log In',
    submittingButton: 'Logging in...',
    noAccount: 'Don\'t have an account? Sign up',
    partnerRedirect: 'Partners must log in at partner.insulacharter.it',
    testAccounts: 'Test accounts:',
    testGuest: 'Guest: turista@example.com',
    testAdmin: 'Admin: info@insulacharter.it',
    testPassword: 'Password: InsulaHost2026!',
    forgotPassword: 'Forgot password?',
  },

  // ============================================
  // AUTH - FORGOT PASSWORD
  // ============================================
  forgotPassword: {
    title: 'Forgot password',
    description: 'Enter your email and we\'ll send you a link to reset your password.',
    submitButton: 'Send reset link',
    emailSentTitle: 'Email sent!',
    emailSentDescription: 'If the email is registered, you will receive a link to reset your password. Check your spam folder too.',
    backToLogin: 'Back to login',
  },

  // ============================================
  // AUTH - RESET PASSWORD
  // ============================================
  resetPassword: {
    title: 'New password',
    description: 'Choose a new password for your account.',
    newPasswordLabel: 'New password',
    newPasswordPlaceholder: 'Enter your new password',
    confirmPasswordLabel: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat your new password',
    submitButton: 'Reset password',
    successTitle: 'Password reset!',
    successDescription: 'Your password has been updated. You will be redirected to login...',
    invalidLink: 'Invalid link',
    invalidLinkDescription: 'The reset link is missing or invalid. Request a new link.',
    requestNewLink: 'Request new link',
    passwordMismatch: 'Passwords do not match',
  },

  // ============================================
  // AUTH - PARTNER LOGIN
  // ============================================
  partnerLogin: {
    title: 'Partner Portal',
    subtitle: 'InsulaCharter — Restricted Area',
    sectionLabel: 'Partner Login',
    emailPlaceholder: 'email@example.com',
    submit: 'Log In',
    submitting: 'Logging in...',
    accessDenied: 'This area is reserved for partners. Please log in from the main site.',
    copyright: 'All rights reserved.',
  },

  // ============================================
  // PARTNER PROFILE (completion)
  // ============================================
  partnerProfile: {
    tab: { anagrafica: 'Details', documenti: 'Documents', account: 'Account' },
    header: { verified: 'Verified', pending: 'Pending', ibanMissing: 'IBAN not set', manageIban: 'Settings' },
    role: { VENDOR: 'Operator', COMPANY: 'Company', ADMIN: 'Admin' },
    completionTitle: 'Profile completion',
    missing: 'Missing',
    allDone: 'Profile complete',
    loading: 'Loading profile…',
    loadError: 'Error loading profile',
    saved: 'Profile updated',
    saveError: 'Error saving',
    notSet: 'Not set',
    managePayout: 'Manage in Settings → Payments & Payouts',
    save: 'Save profile',
    saving: 'Saving…',
    badgeComplete: 'Complete',
    badgeMissingOne: 'missing',
    badgeMissingMany: 'missing',
    bioPlaceholder: 'Describe your business (min 30 characters)…',
    bioHelper: 'At least 30 characters.',
    seasonHint: 'Default for the whole fleet. Leave empty for "always open".',
    seasonFrom: 'From',
    seasonTo: 'To',
    docsHint: 'Documents are verified by the InsulaCharter team.',
    docsUpload: 'Upload',
    docsReplace: 'Replace',
    docStatusMissing: 'To upload',
    docStatusPending: 'Under review',
    docStatusApproved: 'Verified',
    docStatusRejected: 'Rejected',
    docUploadError: 'Upload error',
    docUploadOk: 'Document uploaded',
    emailVerifyTitle: 'Email verification',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    pwUpdate: 'Update password',
    pwUpdating: 'Updating…',
    pwMismatch: 'New passwords do not match',
    pwTooShort: 'New password must be at least 8 characters',
    pwUpdated: 'Password updated',
    pwError: 'Error changing password',
    forgotTitle: 'Forgot your password?',
    forgotDesc: 'Get a link by email to reset your password.',
    forgotCta: 'Reset password →',
    suspendedOk: 'Bookings suspended',
    reactivatedOk: 'Bookings reactivated',
    section: {
      company: 'Company data',
      address: 'Registered address',
      personal: 'Personal data',
      payout: 'Payout data',
      presentation: 'Presentation',
      documents: 'Business documents',
      season: 'Booking season',
      account: 'Account and security',
    },
    field: {
      company_name: 'Company name',
      company_vat: 'VAT number',
      company_pec: 'Certified email (PEC)',
      company_sdi: 'SDI code',
      vendor_phone: 'Phone',
      address_street: 'Address',
      address_city: 'City',
      address_province: 'Province',
      address_postal_code: 'Postal code',
      payout_iban: 'IBAN',
      payout_holder: 'Account holder',
      vendor_bio: 'Bio / description',
      email_verified: 'Email verified',
      name: 'Name',
      email: 'Email',
      BUSINESS_REGISTRATION: 'Business registration',
      VAT_CERTIFICATE: 'VAT certificate',
      INSURANCE: 'Liability insurance',
    },
  },

  // BOOKING SUSPENDED OVERLAY
  // ============================================
  suspended: {
    title: 'Bookings temporarily suspended',
    description: 'We are updating our booking system. Leave your email and we will notify you as soon as bookings reopen.',
    notifyLabel: 'Want to be notified?',
    emailPlaceholder: 'Your email',
    notifyBtn: 'Notify me when available',
    sending: 'Sending...',
    error: 'An error occurred. Please try again.',
    vendorTitle: 'Partner bookings suspended',
    vendorDescription: 'This partner has temporarily suspended bookings for their boats.',
    boatTitle: 'Boat bookings suspended',
    boatDescription: 'Bookings for this boat are temporarily suspended.',
    suspendVendorBookings: 'Suspend partner bookings',
    unsuspendVendorBookings: 'Resume partner bookings',
    suspendVendorBookingsDesc: 'Block bookings for all boats of this partner.',
    suspendMyBookings: 'Pause my bookings',
    unsuspendMyBookings: 'Resume my bookings',
    suspendMyBookingsDesc: 'Pause bookings for all your boats (e.g. vacation period).',
    suspendBoatBookings: 'Suspend bookings',
    unsuspendBoatBookings: 'Resume bookings',
    byAdmin: 'Suspended by administrator',
  },

  // ============================================
  // COOKIE CONSENT
  // ============================================
  cookie: {
    title: 'Privacy on Board',
    description: 'We use cookies to provide you with an optimal browsing experience and personalise your next itineraries in Sardinia.',
    acceptAll: 'Accept All',
    decline: 'Decline',
    advancedPrefs: 'Advanced Preferences',
    savePrefs: 'Save Preferences',
    alwaysActive: 'always active',
    necessary: 'Necessary',
    necessaryDesc: 'Essential cookies for the website to function. They cannot be disabled.',
    analytics: 'Analytics',
    analyticsDesc: 'Help us understand how you use the site to improve your experience.',
    marketing: 'Marketing',
    marketingDesc: 'Used to show you relevant offers and promotions.',
    preferences: 'Preferences',
    preferencesDesc: 'Remember your choices such as language and browsing area.',
  },

  // ============================================
  // AUTH - REGISTER
  // ============================================
  register: {
    title: 'Create Account',
    subtitle: 'Start your sea adventure',
    nameLabel: 'Full Name',
    namePlaceholder: 'John Smith',
    emailLabel: 'Email',
    emailPlaceholder: 'your@email.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••',
    passwordHelper: 'Min 8 characters, uppercase, lowercase, number, special character',
    confirmPasswordLabel: 'Confirm Password',
    passwordMismatch: 'Passwords do not match',
    submitButton: 'Sign Up',
    submittingButton: 'Signing up...',
    hasAccount: 'Already have an account? Log in',
    // Role selection
    roleLabel: 'Account type',
    roleGuest: 'Tourist',
    roleGuestDesc: 'Book boats and services',
    roleVendor: 'Owner',
    roleVendorDesc: 'Rent out your boat',
    roleAgency: 'Agency',
    roleAgencyDesc: 'Resell boats and services from companies',
    roleCompany: 'Company',
    roleCompanyDesc: 'Manage a fleet of boats',
    roleServiceProvider: 'Service Provider',
    roleServiceProviderDesc: 'Offer services to boats and clients',
    roleServiceProviderDisabled: 'Invite only',
    roleExcursion: 'Excursion Operator',
    roleExcursionDesc: 'Snorkeling, tours, trekking — excursions only',
    providesExcursionsLabel: 'I also offer guided excursions',
    providesExcursionsHelp: 'Check this if your company runs both charter and excursions: you will get access to both dashboards.',
    companyTypeTitle: 'What does your company do?',
    companyTypeSubtitle: 'Pick the activity type to enable the right features',
    serviceTypeTitle: 'What do you offer?',
    serviceTypeSubtitle: 'Pick all the services you offer (you can select more than one)',
    art49Title: 'You are a private individual: occasional charter (art. 49-bis)',
    art49Intro: 'As a private individual without a VAT number you can rent or charter your boat occasionally. Key points:',
    art49P1: 'Up to 42 days per year in total (rental, excursions and experiences).',
    art49P2: 'Favourable tax regime: 20% flat substitute tax on proceeds (on request).',
    art49P3: 'It does not constitute a commercial business activity.',
    art49P4: 'Beyond the threshold a VAT number is required: you must register as a Company.',
    art49Disclaimer: 'Reference: art. 49-bis Italian Pleasure Boating Code (Legislative Decree 171/2005). Indicative information, not tax advice.',
    vatLookupCta: 'Fetch data from VAT',
    vatSearching: 'Searching…',
    vatAutofilled: 'Auto-filled — review and edit if needed',
    vatEnterFirst: 'Enter the VAT number first',
    vatInvalid: 'Invalid VAT number',
    vatError: 'Lookup error',
    vatNetworkError: 'Network error',
    vatLookupHint: 'Enter the VAT number and press “Fetch” to pre-fill company name, PEC and address (you can edit them afterwards).',
    companyTypeCharter: 'Charter / Boat rental',
    companyTypeCharterDesc: 'Manage a fleet of boats for rent',
    companyTypeExcursion: 'Excursions only',
    companyTypeExcursionDesc: 'Snorkeling, guided tours, trekking, tastings — no rental',
    companyTypeMixed: 'Charter + excursions',
    companyTypeMixedDesc: 'You offer both: access to two dashboards from the same partner area',
    serviceProviderSectionTitle: 'Provider Information',
    vendorSectionTitle: 'Owner Information',
    vendorPhoneLabel: 'Phone',
    vendorPhonePlaceholder: '+39 333...',
    companySectionTitle: 'Company Details',
    companyNameLabel: 'Company Name',
    companyNamePlaceholder: 'Company Name Ltd.',
    companyVatLabel: 'VAT Number',
    companyVatPlaceholder: 'IT12345678901',
    companyPecLabel: 'PEC',
    companySdiLabel: 'SDI Code',
    spSectionTitle: 'Provider Information',
    spLicenseLabel: 'License Number',
    spLicensePlaceholder: 'e.g. ABC-12345',
    spServiceAreaLabel: 'Service area',
    spServiceAreaPlaceholder: 'e.g. Alghero, Sassari...',
    spFleetSizeLabel: 'Number of vehicles',
    passwordStrengthWeak: 'Weak',
    passwordStrengthFair: 'Fair',
    passwordStrengthGood: 'Good',
    passwordStrengthStrong: 'Strong',
    pendingApprovalInfo: 'This account type requires approval from the team.',
    pendingApprovalTitle: 'Registration Submitted',
    pendingApprovalMessage: 'Your account has been created successfully. Our team will verify your details and approve your profile.',
    pendingApprovalStep1: '1. Account created successfully',
    pendingApprovalStep2: '2. Awaiting team verification',
    pendingApprovalStep3: '3. You will receive an email notification',
    backToHome: 'Back to home',
  },

  // ============================================
  // MAINTENANCE PAGE
  // ============================================
  maintenance: {
    title: 'Site Under Maintenance',
    message: 'We are updating the site to offer you a better experience. We will be back online shortly.',
    footer: 'InsulaCharter - Boat Rental in Sardinia',
  },

  // ============================================
  // LANDING PAGE (Pre-launch)
  // ============================================
  landing: {
    title: 'InsulaCharter',
    subtitle: 'We are working for you',
    message1: 'Our new site is under development.',
    message2: 'Soon you will be able to discover the best boats for rent in Sardinia.',
    needInfo: 'Need information?',
    devAccess: 'Developer access',
    devLoginTitle: 'Developer Access',
    devLoginMessage: 'Enter the password to access the site',
    wrongPassword: 'Wrong password',
    copyright: '© 2026 InsulaCharter - Boat Rental in Sardinia',
  },

  // ============================================
  // TOAST MESSAGES
  // ============================================
  toast: {
    bookingCreated: 'Booking created successfully!',
    bookingCancelled: 'Booking cancelled successfully!',
    bookingError: 'Error creating booking: ',
    boatCreated: 'Boat added successfully!',
    boatUpdated: 'Boat updated successfully!',
    boatDeleted: 'Boat deleted successfully!',
    maxPhotos: 'You can add a maximum of 10 photos',
    nameMinLength: 'Name must be at least 3 characters',
    descriptionMinLength: 'Description must be at least 10 characters',
    serviceExists: 'A service with this name already exists',
  },

  // ============================================
  // CONFIRM DIALOGS
  // ============================================
  confirm: {
    cancelBooking: 'Do you want to cancel the booking?',
    deleteBoat: 'Do you really want to remove this boat from the InsulaCharter fleet?',
    deleteServiceUsed: 'This service is used in {count} boats. Do you want to delete it anyway?',
    deleteService: 'Do you want to delete this service?',
  },

  // ============================================
  // LANGUAGE SELECTOR
  // ============================================
  language: {
    select: 'Language',
    it: 'Italian',
    en: 'English',
    de: 'German',
  },

  // ============================================
  // CALENDAR
  // ============================================
  calendar: {
    available: 'Available',
    booked: 'Booked',
    pending: 'Pending',
    blocked: 'Unavailable',
    yourSelection: 'Your selection',
    selectDates: 'Select dates',
    pickupDate: 'Pickup',
    returnDate: 'Return',
    days: 'days',
    day: 'day',
    clearSelection: 'Clear selection',
    confirmDates: 'Confirm dates',
    availability: 'Availability',
    selectPickup: 'Select the pickup date',
    selectReturn: 'Select the return date',
    // Booking detail modal
    bookingDetails: 'Booking Details',
    boat: 'Boat',
    client: 'Client',
    period: 'Period',
    passengers: 'passengers',
    total: 'Total',
    basePrice: 'Rental',
    insuranceFee: 'Insurance',
    extras: 'Extras',
    specialRequests: 'Special requests',
    createdAt: 'Created on',
    unavailable: 'Unavailable',
    paymentMethod: 'Payment Method',
  },

  // ============================================
  // ICAL SYNC
  // ============================================
  ical: {
    title: 'iCal Sync',
    titleWithBoat: 'iCal Sync — {boatName}',
    exportUrl: 'Export URL',
    exportHint: 'Use this URL to import the calendar on other platforms (Airbnb, Booking.com, MMK Booking Manager, etc.)',
    importedFeeds: 'Imported Feeds',
    addFeed: 'Add Feed',
    feedUrlPlaceholder: 'iCal feed URL',
    feedLabelPlaceholder: 'Label (optional)',
    cancel: 'Cancel',
    adding: 'Adding...',
    add: 'Add',
    noFeeds: 'No feeds imported',
    events: 'events',
    error: 'Error',
    lastSync: 'Last sync',
    confirmDelete: 'Delete this iCal feed?',
    syncNow: 'Sync now',
    openFeed: 'Open feed',
    deleteFeed: 'Delete feed',
    sourceAirbnb: 'Airbnb',
    sourceBooking: 'Booking.com',
    sourceGoogle: 'Google Calendar',
    sourceMmk: 'MMK Booking Manager',
    sourceOther: 'Other',
    guideTitle: 'How to connect your external calendar',
    guideStep1: '1. Copy the export URL above and paste it in the external platform (MMK, Airbnb, etc.) as an "external calendar" or "iCal feed"',
    guideStep2: '2. In the external platform, copy your vessel\'s calendar URL and paste it below by clicking "Add Feed"',
    guideStep3: '3. Synchronization happens automatically every 15 minutes. You can force it with the "Sync now" button',
    boatsTitle: 'Boats for rental',
    excursionsTitle: 'Excursions & experiences',
    excursionsHint: 'Publish your excursion/experience departures to external calendars (export only).',
    excursionsEmpty: 'No excursions or experiences',
    copyExportUrl: 'Copy',
    manageFeeds: 'Manage feeds',
  },

  // ============================================
  // BOOKING (Bookings)
  // ============================================
  booking: {
    bookingSuspensionToggled: 'Booking suspension status updated',
    confirmed: 'Confirmed',
    pending: 'Pending',
    cancelled: 'Cancelled',
    completed: 'Completed',
    refunded: 'Refunded',
    awaitingPayment: 'Payment due',
    pendingLateBooking: 'Awaiting confirmation',
    payNow: 'Pay now',
    startingPayment: 'Starting…',
    paymentStartFailed: 'Unable to start payment. Please try again.',
    networkErrorRetry: 'Network error. Please try again.',
    modify: 'Modify',
    cancel: 'Cancel',
    receipt: 'Receipt',
    paid: 'Paid',
    notPaid: 'Unpaid',
    partiallyPaid: 'Partial',
    depositPaid: 'Deposit paid',
    depositRefunded: 'Deposit refunded',
    paymentFailed: 'Failed',
    departurePoint: 'Departure point',
    departurePointRequired: 'Select a departure point',
    departureFrom: 'Departure from',
  },

  // ============================================
  // CANCELLATION REQUEST (customer-driven)
  // ============================================
  cancellation: {
    reasonBadWeather: 'Bad weather',
    reasonBadWeatherHint: 'Rough sea, official alert, unsafe conditions',
    reasonHealth: 'Health reasons',
    reasonHealthHint: 'Illness or injury of the booking holder',
    reasonEmergency: 'Emergency',
    reasonEmergencyHint: 'Serious family or work emergency',
    reasonForceMajeure: 'Force majeure',
    reasonForceMajeureHint: 'Uncontrollable events (e.g. lockdowns, bereavement)',
    reasonOther: 'Other reason',
    reasonOtherHint: 'Specify the reason in the description',
    requestSentTitle: 'Cancellation request sent',
    requestSentDesc: 'The vendor is reviewing your request. You will get a reply by email.',
    reasonLabel: 'Reason:',
    rejectedTitle: 'Request not approved',
    rejectedDefaultDesc: 'The vendor rejected the cancellation request.',
    newRequest: 'Send a new request',
    approvedTitle: 'Cancellation approved',
    approvedDesc: 'Any refund due will be processed via the original payment method.',
    requestCta: 'Request cancellation',
    formTitle: 'Cancellation request for good cause',
    formSubtitle: 'Select a reason. The vendor will review the request and may approve or reject it.',
    description: 'Description',
    descriptionPlaceholderOther: 'Explain the reason for the request (required for "Other")',
    descriptionPlaceholderDefault: 'Add details useful to the vendor (optional)',
    minChars: 'Minimum 10 characters',
    cancel: 'Cancel',
    sending: 'Sending…',
    sendRequest: 'Send request',
    sendFailed: 'Sending failed',
    unknownError: 'Unknown error',
  },

  // ============================================
  // REVIEW FORM
  // ============================================
  review: {
    selectRating: 'Select an overall rating.',
    writeComment: 'Write a comment.',
    submitError: 'Error submitting the review.',
    overallRating: 'Overall rating *',
    titleLabel: 'Title (optional)',
    titlePlaceholder: 'Summarize your experience',
    commentLabel: 'Comment *',
    commentPlaceholder: 'Tell us about your experience...',
    detailedRatings: 'Detailed ratings (optional)',
    boatCondition: 'Boat condition',
    itinerary: 'Itinerary',
    valueForMoney: 'Value for money',
    communication: 'Communication',
    sending: 'Sending...',
    submit: 'Submit review',
  },

  // ============================================
  // AMBASSADOR (banner + invite codes)
  // ============================================
  ambassador: {
    requestSent: 'Request sent',
    genericError: 'Error',
    codeCopied: 'Code copied!',
    copyFailed: 'Unable to copy',
    linkCopied: 'Link copied!',
    linkCopyFailed: 'Unable to copy the link',
    youAreAmbassador: 'You are an Ambassador!',
    commissionPct: '{rate}% commission',
    copyCode: 'Copy code',
    copyInviteLink: 'Copy invite link',
    peopleReferred: 'people referred',
    viaCode: ' · {used} via code',
    withYourCode: ' with your code',
    generateLink: 'Generate link',
    pendingTitle: 'Ambassador request pending',
    pendingDesc: 'Our team is reviewing your request',
    rejectedDesc: 'Your ambassador request was not approved.',
    becomeAmbassador: 'Become an Ambassador',
    becomeAmbassadorDesc: 'Earn a commission on every user you bring to the platform',
    sendingRequest: 'Sending...',
    applyRequest: 'Apply to become an Ambassador',
  },

  // ============================================
  // AMBASSADOR CODES MANAGER (multi-code + referrals)
  // ============================================
  ambassadorCodes: {
    roleCustomer: 'Customer',
    roleAgency: 'Agency',
    roleCollaborator: 'Collaborator',
    roleVendor: 'Vendor',
    roleCompany: 'Company',
    roleAdmin: 'Admin',
    roleServiceProvider: 'Provider',
    roleHost: 'Host',
    codeActivated: 'Code activated',
    codeDeactivated: 'Code deactivated',
    codeCreated: 'Code {code} created',
    createError: 'Error creating code',
    loadingCodes: 'Loading codes...',
    myInviteCodes: 'My invite codes',
    newCode: 'New code',
    deactivated: 'Deactivated',
    exhausted: 'Exhausted',
    uses: 'uses',
    reactivate: 'Reactivate',
    deactivate: 'Deactivate',
    peopleBrought: 'People referred',
    noPeopleYet: 'No one referred yet.',
    tableName: 'Name',
    tableType: 'Type',
    tableOrigin: 'Origin',
    tableDate: 'Date',
    manual: 'Manual',
    viaCodeOrigin: 'Code',
    newCodeModalTitle: 'New invite code',
    newCodeModalSubtitle: 'Create a code for a specific campaign',
    creating: 'Creating...',
    createCode: 'Create code',
    label: 'Label',
    optional: '(optional)',
    labelPlaceholder: 'E.g. Summer 2026 campaign',
    labelHint: 'For your organization only — not publicly visible.',
    maxUses: 'Max uses',
    maxUsesPlaceholder: 'Unlimited if empty',
    maxUsesHint: 'How many times the code can be used before it runs out.',
    commissionNote: 'The commission will be inherited from your main code. Only an administrator can change the percentage.',
  },

  // ============================================
  // PROFILE
  // ============================================
  profile: {
    // Page header
    myProfile: 'My Profile',
    manageYourAccount: 'Manage your personal data, documents and payment preferences',

    // Sections
    personalData: 'Personal Data',
    address: 'Residential Address',
    phoneNumber: 'Phone Number',
    documents: 'Identity Documents',
    profilePhoto: 'Profile Photo',

    // Personal data fields
    firstName: 'First Name',
    firstNamePlaceholder: 'Enter your first name',
    lastName: 'Last Name',
    lastNamePlaceholder: 'Enter your last name',
    dateOfBirth: 'Date of Birth',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Select your nationality',
    fiscalCode: 'Tax Code',
    fiscalCodePlaceholder: 'e.g.: RSSMRA80A01H501U',
    fiscalCodeHelper: 'Optional - Only for Italian citizens',

    // Address fields
    street: 'Street/Address',
    streetPlaceholder: 'Via Roma, 123',
    city: 'City',
    cityPlaceholder: 'e.g.: Olbia',
    postalCode: 'Postal Code',
    postalCodePlaceholder: 'e.g.: 07026',
    province: 'Province/State',
    provincePlaceholder: 'e.g.: SS',
    country: 'Country',
    countryPlaceholder: 'Select the country',

    // Phone fields
    mobilePhone: 'Mobile Phone',
    phoneHelper: 'Enter numbers only, without spaces or dashes',

    // Document fields
    documentType: 'Document Type',
    selectDocumentType: 'Select document type...',
    identityCard: 'Identity Card',
    passport: 'Passport',
    drivingLicense: 'Driving License',
    boatLicense: 'Boating License',
    documentNumber: 'Document Number',
    documentNumberPlaceholder: 'e.g.: AB1234567',
    documentExpiry: 'Expiry Date',
    frontImage: 'Front Image',
    backImage: 'Back Image (optional)',
    clickToUpload: 'Click to upload',
    addDocument: 'Add Document',
    uploadDocument: 'Upload Document',
    noDocuments: 'No documents uploaded',
    expiresOn: 'Expires on',
    verified: 'Verified',
    pending: 'Pending verification',
    confirmDeleteDocument: 'Do you want to delete this document?',
    requiredFields: 'Fill in all required fields',
    documentTooLarge: 'The file is too large. Maximum 10MB.',

    // Avatar
    uploadPhoto: 'Upload Photo',
    deletePhoto: 'Remove',
    confirmDeleteAvatar: 'Do you want to remove the profile photo?',
    cropImage: 'Crop Image',
    zoom: 'Zoom',
    rotate: 'Rotate',
    invalidImageType: 'Invalid file type. Use PNG, JPG or PDF.',
    imageTooLarge: 'The image is too large. Maximum 5MB.',

    // Email verification
    emailVerification: 'Email Verification',
    emailVerified: 'Email Verified',
    emailNotVerified: 'Email not verified',
    verify: 'Verify',
    clickToVerify: 'Click to verify',
    resendVerification: 'Resend verification email',
    emailVerificationDescription: 'Verify your email to unlock all the features of your account.',
    sendVerificationEmail: 'Send Verification Email',
    verificationEmailSent: 'Verification email sent! Check your inbox.',

    // User profile page
    loginToViewProfile: 'Log in to view your profile.',
    myBookings: 'Bookings',
    noBookings: 'No bookings',
    unknownBoat: 'Unknown boat',
    guest: 'Guest',
    memberSince: 'Member since',
    bookingDetails: 'Booking Details',
    embarkationPoint: 'Embarkation Point',
    length: 'Length',
    maxCapacity: 'Max Capacity',
    bookingStatus: 'Booking Status',
    totalPaid: 'Total Paid',
    contactSkipper: 'Contact Skipper',

    downloadReceipt: 'Download Receipt',

    // Success/Error messages
    updateSuccess: 'Profile updated successfully!',

    // Profile save (customer form)
    profileUpdated: 'Profile updated',
    saveError: 'Error saving',
    saving: 'Saving…',
    saveProfile: 'Save profile',

    // Tab sections
    documentsTitle: 'Documents',
    documentsSubtitle: 'Upload your ID and boating licence',
    paymentMethodsSubtitle: 'Saved cards and payment methods',
    savedBoats: 'The boats you saved',
    ambassadorTab: 'Ambassador',
    ambassadorProgram: 'Ambassador Program',
    ambassadorProgramDesc: 'Invite friends and earn commissions on rentals',

    // Stats hero
    upcoming: 'Upcoming',
    total: 'Total',
    nextDeparture: 'Next departure',
    today: 'Today',
    tomorrow: 'Tomorrow',
    inDays: 'In {n} days',
    bookingDetailsCta: 'Booking details',

    // Bookings list
    bookingSentSuccess: 'Booking sent successfully',
    bookingSentSuccessDesc: 'You will receive a confirmation email as soon as the owner approves the request.',
    noBookingsYet: 'No bookings yet',
    noBookingsDesc: 'Search for a boat and book your first sea experience.',
    searchBoat: 'Search a boat',
    boatsSection: 'Boats',
    experiencesSection: 'Experiences',
    experienceFallback: 'Experience',
    awaitingOperator: 'Awaiting operator confirmation',
    reviewed: 'Reviewed',
    leaveReview: 'Leave a review',
    reviewSubmittedPending: 'Review submitted, pending approval.',
    leaveReviewModalTitle: 'Leave a review',
    leaveReviewModalSubtitle: 'Your opinion helps other travelers',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    bookingCountOne: '{count} booking',
    bookingCountOther: '{count} bookings',
    notSet: 'Not set',
    exampleCityPlaceholder: 'E.g.: Olbia',
    exampleVatPlaceholder: 'E.g.: 01692360918',
  },

  // ============================================
  // FAVORITES
  // ============================================
  favorites: {
    title: 'Favorites',
    empty: 'No favorites saved',
    emptyHint: 'Tap the heart on boats to save them here.',
    remove: 'Remove from favorites',
    added: 'Added to favorites',
    removed: 'Removed from favorites',
  },

  // ============================================
  // PAYMENTS
  // ============================================
  payments: {
    paymentMethods: 'Payment Methods',
    addMethod: 'Add Method',
    noMethods: 'No saved payment methods',
    addFirstMethod: 'Add your first payment method',
    selectType: 'Select payment type',
    creditCard: 'Credit Card',
    bankTransfer: 'Bank Transfer',
    cash: 'Cash',
    default: 'Default',
    setAsDefault: 'Set as default',
    confirmDelete: 'Do you want to delete this payment method?',

    // Credit Card
    cardNumber: 'Card Number',
    cardHolder: 'Card Holder',
    expiryDate: 'Expiry Date',
    expires: 'Expires',
    cardSecurityNote: 'For your security, we do not store the full card number or the CVV.',

    // PayPal
    paypalEmail: 'PayPal Email',

    // IBAN
    accountHolder: 'Account Holder',
    bankName: 'Bank Name',

    // Common
    label: 'Label',
    labelPlaceholder: 'e.g.: Personal card, Business account',
    optional: 'Optional',
  },

  // ============================================
  // EQUIPMENT (Onboard Equipment)
  // ============================================
  equipment: {
    gps: { label: 'GPS Chartplotter', description: 'GPS navigation system with marine cartography' },
    vhf: { label: 'VHF Radio', description: 'Marine radio for emergency communications' },
    anchor: { label: 'Anchor with Chain', description: 'Complete anchor with chain and rope' },
    lifejackets: { label: 'Life Jackets', description: 'Life jackets for all passengers' },
    bimini: { label: 'Bimini Top', description: 'Sun shade canopy for sun protection' },
    shower: { label: 'Stern Shower', description: 'Fresh water shower at the stern' },
    cooler: { label: 'Portable Cooler', description: 'Portable refrigerator for drinks and food' },
    bluetooth: { label: 'Bluetooth Stereo', description: 'Audio system with Bluetooth connection' },
    ladder: { label: 'Swim Ladder', description: 'Retractable ladder for easy sea access' },
    cushions: { label: 'Sunbathing Cushions', description: 'Padded cushions for sun relaxation' },
  },

  // ============================================
  // EXTRAS (Extra Services)
  // ============================================
  extras: {
    skipper: { label: 'Professional Skipper', description: 'Experienced captain for navigation' },
    hostess: { label: 'Onboard Hostess', description: 'Assistance and service on board' },
    fuel: { label: 'Full Fuel Tank', description: 'Full tank included in the price' },
    snorkeling: { label: 'Snorkeling Kit', description: 'Mask, snorkel and fins' },
    sup: { label: 'Stand Up Paddle', description: 'SUP board with paddle' },
    towable: { label: 'Towable Tube', description: 'Inflatable towable for fun' },
  },

  // ============================================
  // EXCURSION OPERATOR DASHBOARD
  // ============================================
  excursion: {
    dashboard: 'Excursion Panel',
    dashboardTitle: 'Excursion Dashboard',
    dashboardTitleCompany: 'Operator Dashboard',
    dashboardSubtitle: 'Manage catalog, slots and bookings',
    menuMain: 'Main',
    menuFleet: 'Catalog',
    menuOperations: 'Operations',
    menuFinance: 'Finance',
    menuSettings: 'Settings',
    fleet: 'Excursion catalog',
    calendar: 'Calendar',
    availability: 'Availability',
    pricing: 'Pricing',
    extras: 'Add-ons',
    bookings: 'Bookings',
    requests: 'Special requests',
    customRequests: 'Requests',
    clients: 'Clients',
    reviews: 'Reviews',
    coupons: 'Coupons',
    team: 'Team',
    earnings: 'Earnings',
    earningsByChannel: 'Earnings by channel',
    reports: 'Reports',
    notifications: 'Notifications',
    profile: 'Profile',
    settings: 'Settings',
    guide: 'Operator guide',
    roleCompany: 'Company',
    roleVendor: 'Operator',
  },
  partner: {
    menuServices: 'Services',
    locazione: 'Rental',
    escursioni: 'Excursions',
    esperienze: 'Experiences',
    disponibilita: 'Availability',
    calendarioPartenze: 'Departures calendar',
  },

  // ============================================
  // EXCURSION FLOW — public release (modal, magic-link, V2 catalog)
  // ============================================
  customRequests: {
    title: 'Quote requests',
    subtitle: 'Tailor-made price requests from customers',
    refresh: 'Refresh',
    filterActionable: 'To handle',
    filterAll: 'All',
    empty: 'No quote requests yet.',
    deletedExcursion: 'Removed experience',
    status: {
      PENDING: 'New',
      VENDOR_PROPOSED: 'Proposal sent',
      CUSTOMER_COUNTER: 'Customer counter',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      EXPIRED: 'Expired',
      CONVERTED: 'Booked',
    },
    drawerTitle: 'Request detail',
    noActivity: 'No activity yet.',
    you: 'You',
    customer: 'Customer',
    validUntil: 'Valid until',
    slotStart: 'Start',
    slotEnd: 'End',
    sendProposal: 'Send proposal',
    proposalSent: 'Proposal sent to the customer.',
    priceInvalid: 'Enter a valid price.',
    pricePlaceholder: 'Price €',
    notesPlaceholder: 'Notes for the customer (optional)',
    messagePlaceholder: 'Write a message…',
    reject: 'Reject request',
    rejected: 'Request rejected.',
    rejectReasonPlaceholder: 'Reason (optional)',
    confirmReject: 'Confirm rejection',
  },
  myRequests: {
    title: 'My requests',
    subtitle: 'Quotes requested and proposals received',
    back: 'Back to requests',
    empty: 'You have no quote requests yet.',
    deletedExcursion: 'Removed experience',
    status: {
      PENDING: 'Awaiting reply',
      VENDOR_PROPOSED: 'Proposal received',
      CUSTOMER_COUNTER: 'Counter sent',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      EXPIRED: 'Expired',
      CONVERTED: 'Booked',
    },
  },
  excursionFlow: {
    catalogKicker: 'Sardinia · experiences',
    catalogTitlePart1: 'What the',
    catalogTitleAccent: 'island',
    catalogTitlePart2: 'never tells you in guidebooks.',
    catalogSubtitle: 'Guided itineraries and activities curated by local operators — from trekking to boat tours, snorkeling to wine tastings.',
    catalogCounterLabel: 'Active experiences',
    catalogFilterAll: 'All',
    catalogEmptyTitle: 'We\'re curating the next experiences.',
    catalogEmptyBody: 'Excursions for the upcoming season will be published shortly. In the meantime you can explore the available boats for rent.',
    catalogEmptyCta: 'View boats',
    catalogEmptyFilterTitle: 'No excursion in this category.',
    catalogEmptyFilterBody: 'Try removing the filter to see all experiences.',
    catalogEmptyFilterCta: 'View all',

    cardDiscover: 'Discover',
    cardFeaturedBadge: 'Featured',
    cardOnRequest: 'On request',
    cardFrom: 'From',
    cardPerPerson: 'per person',
    cardPerGroup: 'per group',
    cardMaxPeople: 'max',

    specialRequestCta: 'Make a special request',
    modalTitle: 'Special request',
    modalTypeLabel: 'Request type',
    typeTimeCustomLabel: 'Custom time',
    typeTimeCustomDesc: 'Same tour, different departure time',
    typeGroupCustomLabel: 'Non-standard group',
    typeGroupCustomDesc: 'Group size outside the standard capacity',
    typePrivateLabel: 'Private tour',
    typePrivateDesc: 'Just you, no other participants',
    typeFullyCustomLabel: 'Fully tailored experience',
    typeFullyCustomDesc: 'Different itinerary / duration / content',
    fieldDateStart: 'Preferred date',
    fieldDateEnd: 'End date (if multi-day)',
    fieldPeople: 'People',
    fieldMessage: 'Message for the operator',
    fieldMessagePlaceholder: 'E.g. We\'d like to leave at 4pm instead of the standard time, we\'re a family with 2 kids aged 8 and 10…',
    fieldContactsHeading: 'Your contact details',
    fieldName: 'Full name',
    fieldEmail: 'Email',
    fieldPhone: 'Phone (optional)',
    btnCancel: 'Cancel',
    btnSendRequest: 'Send request',
    successTitle: 'Request sent',
    successBody: 'Our operator will be notified and will reply by email to {email} with a tailored proposal. Please also check your spam folder.',
    successCta: 'Got it',
    disclaimer: 'You\'ll get a reply by email within a few hours. The request is not binding: you can accept or decline the operator\'s final proposal.',

    magicHeroKicker: 'Special request',
    magicHeroGreeting: 'Hi {name}, here\'s the proposal from our operator.',
    magicProposalLabel: 'Vendor proposal',
    magicProposalTotal: 'total',
    magicProposalWhen: 'Date/time',
    magicProposalValidUntil: 'Valid until',
    magicProposalNotes: 'Details',
    magicAcceptCta: 'Accept and pay',
    magicRejectCta: 'Decline',
    magicCounterCta: 'Counter-offer',
    magicCounterTitle: 'Counter-offer',
    magicCounterSubmit: 'Send',
    magicAcceptConfirm: 'Confirm you want to accept the proposal? You\'ll be redirected to payment.',
    magicRejectPrompt: 'Reason (optional, visible to vendor):',
    magicRejectConfirm: 'Confirm decline?',
    magicLinkExpiredTitle: 'Invalid link',
    magicLinkExpiredBody: 'Request not found or link expired',
    magicMyRequest: 'Your request',
    magicConversation: 'Conversation',
    magicMsgPlaceholder: 'Write to the vendor…',
    magicValidUntilFooter: 'Link valid until {date}.',
    magicCreateAccount: 'Create an account',
    magicCreateAccountSuffix: 'to manage all your requests.',
    magicStatusAccepted: 'Proposal accepted. Proceed to payment to confirm.',
    magicStatusConverted: 'Payment completed. Your excursion is confirmed.',
    magicStatusRejected: 'Proposal declined.',
    magicStatusExpired: 'Request expired.',
  },

  // ============================================
  // VENDOR DASHBOARD
  // ============================================
  vendor: {
    earningsByChannel: 'Earnings by channel',
    // Dashboard
    dashboard: 'Dashboard',
    dashboardTitle: 'Vendor Dashboard',
    dashboardTitleCompany: 'Company Dashboard',
    dashboardSubtitle: 'Manage your boats and bookings',

    // Sidebar menu groups
    menuMain: 'Main',
    menuFleet: 'Fleet',
    menuOperations: 'Operations',
    menuFinance: 'Finance',
    menuSettings: 'Settings',
    // Sidebar menu items
    fleet: 'Boats',
    pricing: 'Pricing',
    bulk: 'Bulk operations',
    suspensions: 'Suspensions',
    extras: 'Extra Services',
    bookings: 'Bookings',
    customRequests: 'Requests',
    clients: 'Clients',
    reviews: 'Reviews',
    coupons: 'Coupons',
    team: 'Team',
    network: 'Network & Partners',
    earnings: 'Earnings',
    reports: 'Reports',
    analytics: 'Analytics',
    notifications: 'Notifications',
    profile: 'Profile',
    settings: 'Settings',
    guide: 'Guide',
    roleVendor: 'Boat Owner',
    roleCompany: 'Company',

    // Navigation (legacy)
    myBoats: 'My boats',
    myBookings: 'Received bookings',
    myEarnings: 'Earnings',
    myClients: 'My clients',
    myExtras: 'Extra Services',
    myProfile: 'Vendor Profile',
    calendar: 'Calendar',

    // Tabs
    tabDashboard: 'Dashboard',
    tabMyBoats: 'My boats',
    tabCalendar: 'Calendar',
    tabBookings: 'Bookings',
    tabEarnings: 'Earnings',
    tabClients: 'Clients',
    tabProfile: 'Profile',
    tabCompanyProfile: 'Company data',

    // Stats
    activeBoats: 'Active boats',
    totalBookings: 'Bookings',
    totalRevenue: 'Total Revenue',
    totalPayout: 'Net Payout',
    platformFees: 'Platform Fees',
    bookingCount: 'Bookings',
    netEarnings: 'Net earnings',
    pendingPayout: 'Pending payout',
    paidOut: 'Already paid',
    occupancyRate: 'Occupancy Rate',
    revenuePerBoat: 'Avg Revenue/Boat',
    loadingStats: 'Loading statistics...',
    commissionUpdated: 'Commission updated',

    // Boat List
    loadingBoats: 'Loading boats...',
    noBoats: 'No boats',
    noBoatsHint: 'Add your first boat from the admin dashboard',
    boatsCount: 'boats',
    boatActive: 'Active',
    boatInactive: 'Inactive',
    approvalApproved: 'Approved',
    approvalPending: 'Under review',
    approvalRejected: 'Rejected',
    approvalNotes: 'Notes:',
    loadBoatsError: 'Error loading boats',

    // Calendar
    calendarTitle: 'Booking Calendar',
    calendarSubtitle: 'View and manage bookings for your boats',
    loadingCalendar: 'Loading calendar...',
    noBoatsCalendar: 'Add a boat to see the calendar',
    allBoats: 'All boats',
    blockDates: 'Block dates',
    blockDatesTitle: 'Block dates',
    blockingFor: 'Blocking for',
    startDate: 'Start date',
    endDate: 'End date',
    blockReason: 'Reason (optional)',
    blockReasonPlaceholder: 'E.g.: maintenance, personal use...',
    blockWarning: 'Blocked dates will prevent new bookings for the selected period',
    confirmBlock: 'Confirm block',
    datesBlocked: 'Dates blocked successfully',
    datesUnblocked: 'Dates unblocked',
    blockError: 'Error blocking dates',
    unblockError: 'Error unblocking dates',
    selectBoatFirst: 'Select a boat first',
    selectDatesFirst: 'Select the dates to block',
    blockedPeriods: 'Blocked periods',
    unblock: 'Unblock',
    fleetOverview: 'Fleet overview',
    confirmed: 'confirmed',
    pendingLabel: 'pending',
    blocked: 'blocked',
    noBookingsYet: 'No bookings',
    calendarLoadError: 'Error loading calendar data',

    // Bookings
    loadingBookings: 'Loading bookings...',
    allStatuses: 'All statuses',
    pendingBookings: 'Pending',
    confirmedRevenue: 'Confirmed revenue',
    bookingsTitle: 'Bookings',
    bookingsSubtitle: 'Manage bookings',
    noBookingsFound: 'No bookings found',
    allPaymentStatuses: 'All payments',
    paymentPending: 'Unpaid',
    paymentPaid: 'Paid',
    paymentPartial: 'Partial',
    paymentRefunded: 'Refunded',
    calendarView: 'Calendar',
    listView: 'List',
    searchGuest: 'Search client...',
    downloadReceipt: 'Download receipt',
    reversibleBookings: 'Reversible Bookings',
    reversibleBookingsSubtitle: 'You can restore the previous status',
    boatsOverview: 'Boats Overview',
    cancelWithReason: 'Cancel with reason',
    cancelConfirmMessage: 'Are you sure you want to cancel this booking?',
    cancelReasonPlaceholder: 'Cancellation reason (optional)',
    bookingDetail: 'Booking Detail',
    guestInfo: 'Client Information',
    boatInfo: 'Boat Information',
    rentalDetails: 'Rental Details',
    experienceDetails: 'Experience details',
    priceSummary: 'Price Summary',
    basePrice: 'Base price',
    insuranceFee: 'Insurance',
    specialRequests: 'Special Requests',
    cancellationReason: 'Cancellation Reason',
    reverse: 'Restore',
    undo: 'Undo',
    statusPending: 'Pending',
    statusConfirmed: 'Confirmed',
    statusCompleted: 'Completed',
    statusCancelled: 'Cancelled',
    noBookings: 'No bookings',
    boatFallback: 'Boat',
    guestFallback: 'Guest',
    nights: 'nights',
    guests: 'guests',
    netPayout: 'Net:',
    loadBookingsError: 'Error loading bookings',

    // Earnings
    loadingEarnings: 'Loading earnings...',
    platformCommission: 'Platform commission',
    earningsDetail: 'Earnings detail',
    headerBoat: 'Boat',
    headerDates: 'Dates',
    headerTotal: 'Total',
    headerCommission: 'Commission',
    headerNet: 'Net',
    headerPayout: 'Payout',
    payoutPaid: 'Paid',
    payoutPending: 'Pending',

    // Client Management
    searchClient: 'Search client...',
    noClients: 'No clients',
    noClientsHint: 'Clients will appear here after the first booking',
    bookingsLabel: 'Bookings',
    spentLabel: 'Spent',
    loadClientsError: 'Error loading clients',

    // Vendor Profile
    profileTitle: 'Vendor Profile',
    profileUpdated: 'Profile updated successfully',
    firstName: 'First Name',
    firstNamePlaceholder: 'John',
    lastName: 'Last Name',
    lastNamePlaceholder: 'Smith',
    phone: 'Phone',
    phonePlaceholder: '+39 333 1234567',
    bio: 'Bio',
    bioPlaceholder: 'Introduce yourself to your clients...',
    vendorBio: 'Bio / Description',
    vendorPhone: 'Phone',
    payoutData: 'Payout Data',
    iban: 'IBAN',
    ibanPlaceholder: 'IT60X0542811101000000123456',
    payoutIban: 'Payout IBAN',
    accountHolder: 'Account holder',
    accountHolderPlaceholder: 'John Smith',
    payoutHolder: 'Account holder',
    saveProfile: 'Save profile',
    loadProfileError: 'Error loading profile',
    saveProfileError: 'Error saving',

    // Company Profile
    companyData: 'Company Data',
    companyName: 'Company name',
    companyNamePlaceholder: 'Sardinia Charter S.r.l.',
    vatNumber: 'VAT Number',
    companyVat: 'VAT Number',
    vatPlaceholder: 'IT12345678901',
    sdiCode: 'SDI Code',
    companySdi: 'SDI Code',
    sdiPlaceholder: 'M5UXCR1',
    pec: 'Certified Email (PEC)',
    companyPec: 'Certified Email (PEC)',
    pecPlaceholder: 'company@pec.it',
    companyPhone: 'Company phone',
    companyPhonePlaceholder: '+39 070 1234567',
    officeAddress: 'Office address',
    street: 'Street',
    streetPlaceholder: 'Via Roma 1',
    city: 'City',
    cityPlaceholder: 'Olbia',
    province: 'Province',
    provincePlaceholder: 'SS',
    postalCode: 'Postal Code',
    postalCodePlaceholder: '07026',
    presentation: 'Presentation',
    companyBioPlaceholder: 'Describe your company to clients...',
    saveCompanyProfile: 'Save company profile',
    companyProfileUpdated: 'Company profile updated',
    perDay: '/day',
    saving: 'Saving...',

    // Booking as guest
    bookingOptions: 'Booking Options',
    canBookAsGuest: 'I also want to book boats',
    canBookAsGuestDesc: 'Enable this option to be able to book boats from other vendors as a client.',
    alsoClient: 'Also a client',

    // Extras Management
    tabExtras: 'Extra Services',
    extrasTitle: 'My Extra Services',
    extrasSubtitle: 'Manage additional services for your boats',
    addExtra: 'New Extra',
    editExtra: 'Edit Extra',
    noExtras: 'No extra services',
    noExtrasHint: 'Create your first extra service to offer it to clients',
    extraKey: 'Key (unique ID)',
    extraKeyPlaceholder: 'e.g.: skipper_service',
    extraLabel: 'Service Name',
    extraLabelPlaceholder: 'e.g.: Skipper Service',
    extraDescription: 'Description',
    extraDescPlaceholder: 'Describe the extra service...',
    extraPrice: 'Default Price (€)',
    extraPriceType: 'Price Type',
    pricePerStay: 'Per rental',
    pricePerDay: 'Per day',
    pricePerPerson: 'Per person',
    pricePerPersonDay: 'Per person/day',
    extraStatusApproved: 'Approved',
    extraStatusPending: 'Under review',
    extraStatusRejected: 'Rejected',
    approvalWarning: 'Editing an approved extra will resubmit it for admin approval.',
    editResetsApproval: 'Editing an approved extra sends it back for review',
    extraCreated: 'Extra service created. Awaiting approval.',
    extraUpdated: 'Extra service updated',
    extraDeleted: 'Extra service deleted',
    extraSaveError: 'Error saving the extra service',
    extraDeleteError: 'Error deleting the extra service',
    rejectionNotes: 'Rejection notes',
    loadExtrasError: 'Error loading extra services',
  },

  // ============================================
  // COMPANY DASHBOARD
  // ============================================
  company: {
    // Menu groups
    menuMain: 'Main',
    menuFleet: 'Fleet Management',
    menuOperations: 'Operations',
    menuFinance: 'Finance',
    menuSettings: 'Settings',

    // Menu items
    overview: 'Overview',
    fleet: 'Fleet',
    calendar: 'Calendar',
    bookings: 'Bookings',
    clients: 'Clients',
    coupons: 'Coupons',
    team: 'Team',
    earnings: 'Earnings',
    reports: 'Reports',
    companyProfile: 'Company Profile',
    settings: 'Settings',
    companyAccount: 'Company Account',

    // Overview
    welcomeBack: 'Welcome back, {name}',
    overviewSubtitle: 'Overview of your company activities',
    recentBookings: 'Recent Bookings',
    viewAll: 'View all',
    quickActions: 'Quick Actions',
    quickFleet: 'Manage Fleet',
    quickFleetDesc: 'Add and manage your boats',
    quickCalendar: 'Calendar',
    quickCalendarDesc: 'View availability and bookings',
    quickReports: 'Reports',
    quickReportsDesc: 'Analyze earnings and generate exports',

    // Team
    teamSubtitle: 'Manage your operational team',
    teamComingSoon: 'Team Management coming soon',
    teamComingSoonDesc: 'Soon you will be able to add skippers, operators and managers to your company team.',
    plannedRoles: 'Planned Roles',
    roleSkipper: 'Skipper',
    roleSkipperDesc: 'Captains for your boats',
    roleOperator: 'Operator',
    roleOperatorDesc: 'Booking and client management',
    roleManager: 'Manager',
    roleManagerDesc: 'Fleet and operations supervision',

    // Reports
    reportsSubtitle: 'Detailed earnings analysis',
    dateFrom: 'From',
    dateTo: 'To',
    clearFilters: 'Reset',
    totalRevenue: 'Total Revenue',
    totalCommission: 'Commissions',
    totalNet: 'Net',
    noReportData: 'No data for the selected period',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    nights: 'Nights',

    // Export
    exportCSV: 'Export CSV',
    exportPDF: 'Export PDF',
    comingSoon: 'Coming soon',
  },

  // Promo overlay (public)
  promo: {
    close: 'Close',
    discover: 'Discover',
    bookNow: 'Book Now',
    prev: 'Previous',
    next: 'Next',
  },

  // Service Provider System
  sp: {
    dashboardTitle: 'My Services',
    dashboardSubtitle: 'Manage your service requests',
    role: 'Service Provider',
    tabStats: 'Statistics',
    tabRequests: 'Requests',
    tabProfile: 'Profile',
    statsPending: 'Pending',
    statsAccepted: 'Accepted',
    statsCompleted: 'Completed',
    statsRevenue: 'Monthly Revenue',
    statusPENDING: 'Pending',
    statusBROADCAST: 'Broadcasting',
    statusACCEPTED: 'Accepted',
    statusCOMPLETED: 'Completed',
    statusREJECTED: 'Rejected',
    statusCANCELLED: 'Cancelled',
    statusEXPIRED: 'Expired',
    noRequests: 'No service requests',
    requestDetail: 'Request Detail',
    boat: 'Boat',
    guest: 'Guest',
    guestPhone: 'Guest Phone',
    passengers: 'Passengers',
    pickupDate: 'Pickup Date',
    returnDate: 'Return Date',
    pickup: 'Pickup Location',
    dropoff: 'Destination',
    specialRequests: 'Special Requests',
    rejectionReason: 'Rejection Reason',
    rejectionReasonPlaceholder: 'Enter rejection reason...',
    notes: 'Internal Notes',
    notesPlaceholder: 'Add notes...',
    saveNotes: 'Save Notes',
    accept: 'Accept',
    reject: 'Reject',
    complete: 'Complete',
    confirmReject: 'Confirm Rejection',
    profileBasic: 'Basic Information',
    name: 'Name',
    phone: 'Phone',
    license: 'License Number',
    licensePlaceholder: 'E.g. OT/12345',
    bio: 'Description',
    fleet: 'Vehicle Fleet',
    fleetSize: 'Number of Vehicles',
    maxPassengers: 'Max Passengers',
    vehicleTypes: 'Vehicle Types',
    vehiclePlaceholder: 'E.g. Sedan, 8-seat Minivan',
    serviceArea: 'Service Areas',
    serviceAreaPlaceholder: 'E.g. Olbia, Porto Cervo',
    operatingHours: 'Operating Hours',
    saveProfile: 'Save Profile',
    // Calendar tab
    tabCalendar: 'Calendar',
    calendarTitle: 'Appointments Calendar',
    calendarRequests: 'requests',
    // Extras tab
    tabExtras: 'Services',
    myExtras: 'My Services',
    createExtra: 'New Service',
    editExtra: 'Edit Service',
    extrasEmpty: 'No services created',
    extraPendingInfo: 'New services are sent for approval. Editing an approved service returns it to review.',
    extraEditWarning: 'Editing an approved service will return it to "Under review" for re-approval.',
    extraDeleteConfirm: 'Are you sure you want to delete',
    extraApproved: 'Approved',
    extraPendingReview: 'Under review',
    extraRejected: 'Rejected',
    extraKeyLabel: 'Key (unique ID)',
    extraNameLabel: 'Service Name',
    extraDescLabel: 'Description',
    extraPriceLabel: 'Default Price (€)',
    extraPriceTypeLabel: 'Price Type',
    extraSave: 'Save Changes',
    extraCategory: 'Category',
    extraCategoryMobility: 'Mobility (rentals & transfers)',
    extraCategoryOther: 'Other service',
    extraPorts: 'Operating ports *',
    extraPortsHint: 'Select the ports where you offer this service. Customers will only see the service for boats moored at these ports.',
    extraPortsEmpty: 'No ports available',
    extraPortsRequired: 'Select at least one operating port',
    extraPortsSelectedOne: 'port selected',
    extraPortsSelectedMany: 'ports selected',
    extraOperatingAt: 'Operating at',
    pricingMode: 'Pricing mode',
    pricingModeFixed: 'Fixed price',
    pricingModeQuote: 'On request (quote)',
    pricingModeQuoteHint: 'Customers will request a quote instead of seeing a price. You will receive each request in your dashboard.',
    priceNotApplicable: 'Not applicable',
    // Marketplace broadcast (Phase 1)
    broadcastTitle: 'Live marketplace',
    broadcastHint: 'First to accept wins the service. Be fast!',
    claimNow: 'Claim now',
    claimSuccess: 'Request accepted!',
    claimLost: 'Already taken by another provider',
    // Empty state — new SP with no linked ServiceProvider profile yet
    noProfileYet: 'You do not have an active provider profile yet',
    noProfileHint: 'Wait for the admin to assign you a service, or subscribe to a service template from the "My Offers" tab to start receiving marketplace requests.',
    pendingApprovalTitle: 'Account Pending Approval',
    pendingApprovalMessage: 'Your account is being reviewed. You will be notified when approved.',
    // Generic service keys
    serviceType: 'Service Type',
    bikeRental: 'Bike Rental',
    ebikeRental: 'E-Bike Rental',
    scooterRental: 'Scooter Rental',
    monopattinoRental: 'Kick Scooter Rental',
    transferPorto: 'Port Transfer',
    allServices: 'All Services',
    filterByService: 'Filter by service',
    requestFrom: 'Request from',
    quantity: 'Quantity',
    serviceDate: 'Service Date',
    providerNotes: 'Provider Notes',
    // Error / toast messages
    errLoadRequests: 'Unable to load service requests',
    errLoadStats: 'Unable to load statistics',
    errLoadCalendar: 'Unable to load calendar',
    errLoadProfile: 'Unable to load profile',
    errSaveProfile: 'Unable to save profile',
    errAcceptRequest: 'Unable to accept request',
    errRejectRequest: 'Unable to reject request',
    errCompleteRequest: 'Unable to complete request',
    errSaveNotes: 'Unable to save notes',
    profileSaved: 'Profile saved successfully',
    notesSaved: 'Notes saved',
    capacityCheck: 'Automatic capacity check',
    capacityCheckEnabled: 'Auto-block enabled',
    capacityCheckDisabled: 'Auto-block disabled',
    capacityCheckHintEnabled: 'When enabled, the system automatically rejects new requests if you have already reached the maximum capacity configured for that date.',
    capacityCheckHintDisabled: 'You will receive all requests even beyond the configured capacity and will manually handle any conflicts.',
    capacityCheckSaved: 'Capacity setting updated',
    capacityExhausted: 'Provider has no availability for the requested date',
    // Payment collection mode
    paymentMode: 'Payment collection mode',
    paymentModeFull: 'Platform collects everything',
    paymentModeFullDesc: 'The platform charges the full booking amount and pays you the net via bank transfer.',
    paymentModeCommissionOnly: 'Platform collects commission only',
    paymentModeCommissionDesc: 'The platform charges only its commission. The remaining balance is paid directly to you by the guest at check-in (cash or POS).',
    paymentModeFullHint: 'Standard mode: everything flows through the platform and you receive the net via bank transfer.',
    paymentModeCommissionHint: 'In this mode guests will pay the balance directly to you at the port. The platform only retains its percentage.',
    spIban: 'Payout IBAN',
    spIbanHolder: 'Account holder',
    paymentModeSaved: 'Payment collection mode saved',
    // SP catalog (offers tab)
    tabOffers: 'My offers',
    offersTitle: 'Offer catalog',
    offersDesc: 'Subscribe to service templates created by the admin with your own pricing',
    offersAdd: 'Add offer',
    offersEdit: 'Edit offer',
    offersDelete: 'Delete offer',
    offersSelectTemplate: 'Select template',
    offersCustomPrice: 'Custom price',
    offersCapacity: 'Capacity',
    offersNotes: 'Notes',
    offersPorts: 'Operating ports',
    offersEmpty: 'No active offers. Add one to get started.',
    offersNoTemplates: 'No templates available. The admin must create at least one.',
    offersCreated: 'Offer created',
    offersUpdated: 'Offer updated',
    offersDeleted: 'Offer deleted',
  },

  // ============================================
  // SERVICES SUBDOMAIN
  // ============================================
  servicesLogin: {
    title: 'Insula Services',
    subtitle: 'Service Provider Area',
    sectionLabel: 'Provider Access',
    submit: 'Sign In',
    submitting: 'Signing in...',
    accessDenied: 'This area is reserved for service providers.',
    copyright: 'All rights reserved.',
  },
  servicesCatalog: {
    title: 'Available Services',
    subtitle: 'Our local service partners',
    noProviders: 'No providers available at this time',
    becomeProvider: 'Become a Provider',
    requestService: 'Request a Service',
    serviceArea: 'Service Areas',
    vehicleTypes: 'Vehicles',
    maxPassengers: 'Max Passengers',
    howItWorks: 'How It Works',
    howItWorksDesc: 'Book a boat trip and add the transfer service. A local provider will take care of your transport.',
  },
  servicesNavbar: {
    dashboard: 'Dashboard',
    catalog: 'Service Catalog',
    login: 'Sign In',
    logout: 'Sign Out',
  },

  // ============================================
  // SP SELF-REGISTRATION WIZARD
  // ============================================
  spReg: {
    title: 'Become a Service Provider',
    subtitle: 'Join the Insula Services platform in a few steps.',
    becomeProvider: 'Become a Provider',
    step1Title: 'Account',
    step2Title: 'Business details',
    step3Title: 'Categories & ports',
    step4Title: 'Review',
    next: 'Next',
    previous: 'Back',
    submit: 'Submit request',
    fieldName: 'Full name',
    fieldEmail: 'Email',
    fieldPassword: 'Password',
    fieldPasswordConfirm: 'Confirm password',
    fieldPhonePrefix: 'Prefix',
    fieldPhoneNumber: 'Phone',
    fieldCompanyName: 'Company name',
    fieldVAT: 'VAT number',
    fieldTaxCode: 'Tax code',
    fieldAddress: 'Address',
    fieldCity: 'City',
    fieldProvince: 'Province',
    fieldPostalCode: 'Postal code',
    selectCategories: 'Service categories offered',
    selectPorts: 'Operating ports',
    summaryTitle: 'Review your details',
    summaryHint: 'Make sure everything is correct before submitting your registration request.',
    successTitle: 'Request submitted',
    successMessage: 'Your registration has been received. An administrator will review it shortly and you will be notified by email.',
    errors: {
      required: 'This field is required',
      emailInvalid: 'Invalid email',
      passwordWeak: 'Password must be at least 8 characters',
      passwordMismatch: 'Passwords do not match',
      selectCategory: 'Select at least one category',
      selectPort: 'Select at least one port',
      emailExists: 'This email is already registered',
      generic: 'Something went wrong. Please try again.',
    },
  },

  boatWizard: {
    useType: {
      label: 'Intended use',
      locazione: { label: 'Bareboat rental', desc: 'Self-drive rental (bareboat): appears in the rental catalog' },
      excursionOnly: { label: 'Excursions only', desc: 'Used only for excursions/experiences, excluded from the rental catalog' },
    },
    pricingMode: {
      label: 'Pricing mode',
      fixed: { label: 'Fixed price', desc: 'Visible pricing: customers see prices and can book directly' },
      quoteOnRequest: { label: 'Quote on request', desc: 'Customers send a quote request; you reply with a custom price' },
      priceFieldsHidden: 'Price fields are hidden because the boat is set to "Quote on request".',
    },
  },

};

export default en;
